// @ts-nocheck

/*eslint-disable import/namespace*/
import React from 'react';
import * as Yup from 'yup'; // NOSONAR
import {
  isEmpty,
  toNumber,
  isArray,
  uniq,
  flatten,
  isString,
  cloneDeep,
} from 'lodash';
import moment from 'moment';
import { ContentState, convertFromRaw, EditorState } from 'draft-js';
import { matchPath } from 'react-router-dom';
import { utils as SolyticsUtils } from '@nuva-org/solytics-frontend';
import { Typography, Box, Chip } from '@mui/material';

import { clearAllStorage } from './commonAuthUtils';
import Link from '@mui/material/Link';
import Store from '../store/Store';

import API_ENDPOINTS from '../const/ApiEndPoints';
import REGEX from '../const/Regex';
import SwalToast from '../components/Common/swalToast';
import { PATH_TAGS } from '../const/HelpAndSupport';
import {
  ARRAY_SCHEMA,
  DATE_FORMAT,
  DD_MM_YYYY_DATE_FORMAT,
  HAS_REFRESHED_KEY,
  PARAGRAPH_SCHEMA,
  USERS_WITH_DRAFT,
  LINKED_ENTITIES,
  YYYY_MM_DD_DATE_FORMAT,
  NOOP,
  DATE_FORMAT_HH_MM_SS,
  EMPTY_ARRAY,
  EMPTY_OBJECT,
  ATTRIBUTE_DATA_TYPE_HTML,
  ENTITY_TYPE,
  RELATIONS,
  // initializeReactFlowNodeChoices,
} from '../const/CommonConst';
import { getLabelConfigFile } from './labelConfigUtils';
import { DEFAULT_ROUTE_CONFIG } from '../const/defaultRoutesConfig';
import EntityHealthIndicator from '../components/Common/ModelInventoryAndAssociation/EntityHealthIndicator';
import ColumnHeaderWithFilter from '../components/Common/ModelInventoryAndAssociation/ColumnHeaderWithFilter';
import HealthColumnHeader from '../components/Common/Tables/Headers/HealthColumnHeader';
import AlertHealthIndicator from '../components/Common/ModelInventoryAndAssociation/AlertHealthIndicator';
import ViewChart from '../components/Common/AttributesForm/ViewAttributes/ViewChart';
import ViewTable from '../components/Common/AttributesForm/ViewAttributes/ViewTable';
import ViewAdvancedTable from '../components/Common/AttributesForm/ViewAttributes/ViewAdvancedTable';
import ViewChecklist from '../components/Common/AttributesForm/ViewAttributes/ViewChecklist';
import ViewDocumentAttribute from '../components/Common/AttributesForm/ViewAttributes/ViewDocumentAttribute';
import ViewParagraph from '../components/Common/AttributesForm/ViewAttributes/ViewParagraph';
import ViewTextFieldExtended from '../components/Common/AttributesForm/ViewAttributes/ViewTextFieldExtended';
import SegmentsAttribute from '../components/Common/AttributesForm/FieldComponents/SegmentsAttribute';
import FieldChangeOtherDetails from '../components/Common/AttributesForm/FieldComponents/FieldChangeOtherDetails';
import LinkedEntitiesCell from '../components/Common/ModelInventoryAndAssociation/LinkedEntitiesCell';
import IssueTypeComponent from '../components/Common/AttributesForm/FieldComponents/IssueTypeComponent';
import { LazyHtmlAttributePreviewLink } from '../components/Common/AttributesForm/HtmlPreviewDialogLink';
import { InventoryViewAttribute } from '../components/Common/AttributesForm/FieldComponents';
import AssociationFieldTableViewComponent from '../components/Common/AttributesForm/FieldComponents/AssociationFieldTableViewComponent';

/**
 * Normalizes supported URLs, returning an empty string for removal or null for unsafe input.
 * @param {string} rawValue - Entered or stored URL.
 * @returns {string|null} Normalized URL, removal value, or invalid result.
 */
export const normalizeHyperlink = (rawValue) => {
  const value = (rawValue ?? '').trim();
  if (!value) {
    return '';
  }
  if (REGEX.URL_UNSAFE_CHARACTERS_REGEX.test(value) || value.startsWith('//')) {
    return null;
  }
  if (value.startsWith('#') || value.startsWith('/')) {
    return value;
  }
  const normalized = REGEX.URL_SCHEME_REGEX.test(value)
    ? value
    : `https://${value}`;
  try {
    const url = new URL(normalized);
    if (!['http:', 'https:', 'mailto:', 'tel:'].includes(url.protocol)) {
      return null;
    }
    if (url.username || url.password || (!url.pathname && !url.hostname)) {
      return null;
    }
    return normalized;
  } catch {
    return null;
  }
};

const FILE_SIZE_UNITS = ['Bytes', 'KB', 'MB'];
const FILE_SIZE_BASE = 1024;

/**
 * Converts a size in bytes to a human-readable string (binary units, 1 KB = 1024 Bytes).
 * Largest unit is MB. Up to 2 decimals with trailing zeros omitted (e.g. "149 MB", "1.5 MB").
 * null/undefined/NaN/negative/0 -> "0 KB".
 * @param {number|string|null|undefined} bytes - file size in bytes
 * @returns {string}
 */
export const formatFileSize = (bytes) => {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) {
    return '0 KB';
  }

  let unitIndex = 0;
  let size = value;
  while (size >= FILE_SIZE_BASE && unitIndex < FILE_SIZE_UNITS.length - 1) {
    size /= FILE_SIZE_BASE;
    unitIndex += 1;
  }

  let rounded = Number(size.toFixed(2));
  // Rounding can push a value to 1024 (e.g. 1023.999 KB); promote to next unit
  if (rounded >= FILE_SIZE_BASE && unitIndex < FILE_SIZE_UNITS.length - 1) {
    rounded = Number((rounded / FILE_SIZE_BASE).toFixed(2));
    unitIndex += 1;
  }

  return `${rounded} ${FILE_SIZE_UNITS[unitIndex]}`;
};

/** @param {object|null|undefined} obj - e.g. attribute or form values with `data_type` */
export const isHtmlAttributeType = (obj) =>
  obj?.data_type === ATTRIBUTE_DATA_TYPE_HTML;

/**
 * Case-insensitive substring match for list/search filters.
 * Coerces with String() so API fields that are numbers do not throw on .toLowerCase().
 * @param {*} value
 * @param {string} searchLower - lowercased search query
 */
export const taskFieldMatchesSearch = (value, searchLower) =>
  String(value ?? '')
    .toLowerCase()
    .includes(searchLower);

/**
 * Builds the workflow card label for Update Attribute triggers/actions.
 *
 * The base label is the item name, followed by the selected attribute label
 * when available. Relation text is optional because actions show relation
 * context, while triggers only show the attribute name.
 *
 * @param {object} item - Automation trigger/action object.
 * @param {string} item.name - Trigger/action name shown on the workflow card.
 * @param {object} item.parameters - Trigger/action configuration values.
 * @param {string} [item.parameters.attribute_label] - Selected attribute label.
 * @param {string} [item.parameters.relation] - Action relation type.
 * @param {string} [item.parameters.custom_relation] - Custom relation label.
 * @param {object} [options] - Display options.
 * @param {boolean} [options.includeRelation=false] - Append relation text when present.
 * @returns {string} Formatted workflow display label.
 */
export const getUpdateAttributeDisplayLabel = (
  item,
  { includeRelation = false } = {}
) => {
  const { attribute_label, relation, custom_relation } = item?.parameters || {};
  let label = `${item?.name}`;

  if (attribute_label) {
    label += ` - ${attribute_label}`;
  }

  if (includeRelation && relation) {
    if (relation === 'itself') {
      const relationLabel =
        RELATIONS?.find((r) => r?.value === 'itself')?.label || 'Itself';
      label += ` (${relationLabel})`;
    } else if (relation === 'custom_relation' && custom_relation) {
      label += ` (${custom_relation})`;
    }
  }

  return label;
};

/**
 * Safely parse JSON from localStorage
 * @param {string} key - localStorage key
 * @param {*} defaultValue - default value if parsing fails
 * @returns {*} parsed value or default value
 */
export function safeJsonParse(key, defaultValue = null) {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : defaultValue;
  } catch (error) {
    console.warn(`Failed to parse ${key} from localStorage:`, error);
    return defaultValue;
  }
}

/**
 * Safely parses a JSON string and falls back when parsing fails.
 * @param {string} value
 * @param {*} defaultValue
 * @returns {*}
 */
export function parseSafeString(value, defaultValue = null) {
  if (typeof value !== 'string') {
    return defaultValue;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    console.warn('Failed to parse JSON string:', error);
    return defaultValue;
  }
}

/**
 * Type guard to check if a given value is a valid health status object.
 * A valid health status object must contain 'label', 'color', and 'fail_reason' properties.
 *
 * @param {any} value - The value to check.
 * @returns {boolean} True if the value is a valid health status object, false otherwise.
 */
export const isHealthStatusObject = (value) =>
  value &&
  typeof value === 'object' &&
  'label' in value &&
  'color' in value &&
  'fail_reason' in value;

const getDataType = (inputControlType, dataType) => {
  const dataTypeMap = {
    Character: 'string',
    Integer: 'number',
    Decimal: 'number',
    Date: 'date',
    Paragraph: 'object',
    Checklist: 'object',
    Association: 'object',
    string: 'string',
    number: 'number',
    date: 'date',
    object: 'object',
    Hyperlink: 'string',
    Document: 'array',
    User: 'string',
    AttributeList: 'object',
    ParameterTableList: 'object',
    EmailTemplateList: 'object',
    TemplateList: 'object',
    FieldChangeActions: 'object',
    InventoryView: 'array',
  };

  let jsDataType = dataTypeMap?.[dataType];

  if (inputControlType === 'MULTI_SELECT') {
    jsDataType = 'array';
  }

  return jsDataType;
};

export const createSafeStringValidator = (validator) => {
  return validator.transform((value, originalValue) => {
    // If it's a valid health object, extract its label so it passes string validation.
    if (isHealthStatusObject(originalValue)) {
      return originalValue?.label || '';
    }

    return value;
  });
};

const createYupSchema = (schema = EMPTY_OBJECT, config) => {
  const {
    id,
    name,
    data_type,
    yup_schema,
    input_control_type,
    is_null,
    label,
    is_derived,
    isAttributeDisabled,
  } = config;
  const _schema = cloneDeep(schema);

  if (!isArray(yup_schema)) {
    return schema;
  }

  // If the attribute is derived then just skip the creation of Yup schema for that attribute
  if (is_derived) {
    return schema;
  }

  // If the isAttributeDisabled then just skip the creation of Yup schema for that attribute
  if (isAttributeDisabled) {
    return schema;
  }

  const dataType = getDataType(input_control_type, data_type);
  // Handle nested object types (Paragraph, Checklist etc.)
  if (dataType === 'object' && Array.isArray(yup_schema)) {
    const nestedShape = yup_schema.reduce((acc, fieldConfig) => {
      // recurse: each child must keep its own name
      const childSchema = createYupSchema({}, fieldConfig);
      return { ...acc, ...childSchema };
    }, {});

    let validator = Yup.object().shape(nestedShape);

    if (is_null) {
      validator = validator.nullable().default(null);
    }

    // if parent object itself has a required rule
    const objectRequired = yup_schema.find((v) => v.type === 'required');
    if (objectRequired) {
      validator = validator.required(...objectRequired.params);
    }

    _schema[id ?? name] = validator;
    return _schema;
  }

  if (!Yup[dataType]) {
    return schema;
  }

  let validator = Yup[dataType]();

  // If it's a string schema, we must gracefully handle Health objects
  // since the backend might send a Health object for a Character field.
  // We transform it to its label string ONLY for validation purposes.
  if (dataType === 'string') {
    validator = createSafeStringValidator(validator);
  }

  if (is_null) {
    validator = validator.nullable().default(null);
  }

  if (dataType === 'number') {
    validator = validator
      .min(
        Number.MIN_SAFE_INTEGER,
        `The value falls short of the minimum allowable range ${Number.MIN_SAFE_INTEGER}`
      )
      .max(
        Number.MAX_SAFE_INTEGER,
        `The value exceeds the maximum allowable range of ${Number.MAX_SAFE_INTEGER}`
      );
  }
  if (dataType === 'date') {
    validator = validator.typeError(`${label} must be a valid date`);
  }

  // apply validations
  if (Array.isArray(yup_schema)) {
    yup_schema.forEach((rule) => {
      const { type, params, vendor } = rule;
      if (type === 'when' && vendor === 'HDFC') {
        const [dependentKey, condition] = params;
        validator = validator.when(dependentKey, (value, whenSchema) => {
          const now = new Date();

          const valuesMap = Array.isArray(dependentKey)
            ? dependentKey.reduce((acc, key, index) => {
                // eslint-disable-next-line no-param-reassign
                acc[String(key)] = value?.[index];
                return acc;
              }, {})
            : { [String(dependentKey)]: value };

          const applyRule = (whenRule) => {
            // return if type does not exist
            if (!whenRule?.type) {
              return whenSchema;
            }
            /**
             * below check is specific to HDFC,
             * where we will add this key in attribute schema to check next months
             */
            const [months, message] = whenRule.params;
            const maxDate = new Date(now);
            maxDate.setMonth(now.getMonth() + months);
            return whenSchema[whenRule.type](maxDate, message);
          };

          const evaluateCondition = (cond) => {
            if (cond.operator && cond.conditions) {
              if (cond.operator === 'AND') {
                return cond.conditions.every(evaluateCondition);
              } else if (cond.operator === 'OR') {
                return cond.conditions.some(evaluateCondition);
              }
            }

            const fieldValue = valuesMap[String(cond.key)];
            return cond.is === fieldValue;
          };

          let matched;
          if (condition.operator && condition.conditions) {
            if (condition.operator === 'AND') {
              matched = condition.conditions.every(evaluateCondition);
            } else if (condition.operator === 'OR') {
              matched = condition.conditions.some(evaluateCondition);
            }
          } else {
            matched = condition.conditions.every((cond) => {
              const fieldValue = valuesMap[String(cond.key)];
              return cond.is === fieldValue;
            });
          }

          if (matched) {
            return applyRule(condition.then);
          } else {
            return condition.otherwise
              ? applyRule(condition.otherwise)
              : whenSchema;
          }
        });
      } else if (validator[type]) {
        validator = validator[type](...(params || []));
      }
    });
  }

  _schema[id ?? name] = validator;
  return _schema;
};

/**
 * 📌 Note: This custom implementation is used instead of lodash's `isEmpty` function
 *       due to a known issue with lodash's handling of numbers, false, true values.
 *       Refer to https://github.com/lodash/lodash/issues/496 for more information.
 *
 * The function checks if a value is considered empty, handling various data types.
 * @param value - The value parameter is a variable that can hold any data type (undefined, null,
 * object, string, number, boolean, etc.) and is used to check if it is empty or not.
 * @returns The function `isValueEmpty` takes in a parameter `value` and returns a boolean value. It
 * returns `true` if the value is `undefined`, `null`, an empty object, or a string with only
 * whitespace characters. Otherwise, it returns `false`.
 */

const isValueEmpty = (value) => {
  switch (true) {
    case value === undefined:
    case value === null:
      return true;
    case typeof value === 'object':
      return !Object.keys(value).length;
    case typeof value === 'string':
      return !value.trim().length;
    default:
      return false;
  }
};

const ACCOUNT_ID_STORAGE_KEYS = ['accountId', 'REALM_ID'];

const isStoredValuePresent = (value) =>
  !isValueEmpty(value) && value !== 'null' && value !== 'undefined';

/**
 * Returns the account id from localStorage when one is stored.
 * Checks accountId, then REALM_ID, profile_data.org, and REALM_CONFIG.org.
 * @returns {string|null}
 */
const getAccountId = () => {
  const storedAccountId = ACCOUNT_ID_STORAGE_KEYS.map((key) =>
    localStorage.getItem(key)
  ).find(isStoredValuePresent);

  if (storedAccountId) {
    return storedAccountId;
  }
  return window.CONFIG?.DEFAULT_ACCOUNT_ID;
};

/** Recursively checks if a TipTap doc object has any non-empty text. */
const hasTipTapText = (node) => {
  if (!node) return false;
  if (node.type === 'text') return !!node.text?.trim();
  return (node.content || []).some(hasTipTapText);
};

/**
 * Returns true when rich-text content has no visible text.
 * Handles TipTap JSON (new format), DraftJS JSON (legacy), HTML strings, and empty strings.
 */
export const isRichContentEmpty = (content) => {
  if (!content) return true;
  if (typeof content === 'string') {
    try {
      const parsed = JSON.parse(content);
      // TipTap native JSON
      if (parsed?.type === 'doc') return !hasTipTapText(parsed);
      // Legacy DraftJS JSON
      if (Array.isArray(parsed?.blocks)) {
        return parsed.blocks.every((b) => !b.text?.trim());
      }
    } catch {
      // plain HTML
    }
    return content.replace(/<[^>]*>/g, '').trim() === '';
  }
  if (typeof content === 'object') {
    if (content.type === 'doc') return !hasTipTapText(content);
    if (Array.isArray(content.blocks))
      return content.blocks.every((b) => !b.text?.trim());
  }
  return true;
};

/**
 * Formats an entity label into the style "Entity Name (Unique ID)".
 * If both 'entity_name' (or 'entity_alias') and 'unique_id' are present,
 * they are combined. If only one is present, it returns that value.
 *
 * @param {Object} entity - The entity object to format.
 * @param {string} [entity.entity_name] - The name of the entity.
 * @param {string} [entity.entity_alias] - The alias of the entity.
 * @param {string} [entity.unique_id] - The unique identifier of the entity.
 * @returns {string} The formatted label string.
 */
const formatEntityLabel = (entity) => {
  const name = entity?.entity_name || entity?.entity_alias;
  const uniqueId = entity?.unique_id;

  if (name && uniqueId) {
    return `${name} (${uniqueId})`;
  }
  return name || uniqueId || '';
};

/**
 * Rounds a given number to a specified number of decimal places.
 *
 * @param {number} value - The number to be rounded.
 * @param {number} [roundOfNumber=3] - The number of decimal places to round to. Defaults to 3 if not specified.
 * @returns {number} The rounded number.
 *
 * @example
 * roundToDecimals(3.1415926535);
 * // returns 3.142
 *
 * @example
 * roundToDecimals(3.1415926535, 2);
 * // returns 3.14
 *
 * @example
 * roundToDecimals(3.1, 5);
 * // returns 3.1
 */
function roundToDecimals(value, roundOfNumber = 3) {
  if (typeof value === 'number') {
    return parseFloat(value.toFixed(roundOfNumber));
  }
  return value;
}

/**
 * @function isDateValid
 * @description Checks if a date string is valid. Supports both UTC and "yyyy-mm-dd" formats.
 *
 * @param {string} dateString - The date string to validate.
 * @returns {boolean} `true` if the date string is valid, `false` otherwise.
 */
const isDateValid = (dateString) => {
  const FORMAT = [
    DD_MM_YYYY_DATE_FORMAT,
    DATE_FORMAT,
    YYYY_MM_DD_DATE_FORMAT,
    moment.ISO_8601,
  ];
  if (!Number.isNaN(Number(dateString))) {
    return false;
  }
  if (!isString(dateString)) {
    return false;
  }
  return moment(dateString, FORMAT, true).isValid();
};

/**
 * Fetches a specific property (e.g., label, path) from a nested configuration object like LABEL_CONFIG.
 *
 * @param {string} keyPath - The dot-separated key path to the desired object or property (e.g., "MENUS.dashboard").
 * @param {string} [property="label"] - The property to retrieve from the target object (e.g., "label", "path").
 *                                      Defaults to "label" if not specified.
 * @param {*} [fallback=""] - The fallback value to return if the keyPath or the property does not exist.
 *                            Defaults to an empty string.
 * @returns {*} - The value of the specified property if it exists, otherwise the fallback value.
 *
 * @example
 * // Example 1: Fetch a label
 * getLabelPathFromLabelConfig("MENUS.dashboard"); // Output: "Dashboard"
 *
 * @example
 * // Example 2: Fetch a path
 * getLabelPathFromLabelConfig("MENUS.dashboard", "path"); // Output: "/dashboard"
 *
 * @example
 * // Example 3: Handle missing key with a fallback
 * getLabelPathFromLabelConfig("MENUS.unknownKey", "label", "Default Label"); // Output: "Default Label"
 *
 * @example
 * // Example 4: Fetch an entire nested object
 * getLabelPathFromLabelConfig("MENUS.dashboard", "", {}); // Output: { label: "Dashboard", path: "/dashboard" }
 */
export const getLabelPathFromLabelConfig = (
  keyPath,
  property = 'label',
  fallback = ''
) => {
  const keys = keyPath?.split('.');
  const traverse = (config, remainingKeys) => {
    if (!config || remainingKeys?.length === 0) {
      return config;
    }
    const [currentKey, ...restKeys] = remainingKeys;

    // Handle both direct keys and nested `subPaths`
    const nextConfig = config[currentKey] || config?.subPaths?.[currentKey];
    return traverse(nextConfig, restKeys);
  };
  const result = traverse(getLabelConfigFile(), keys || []);
  return result?.[property] ?? fallback;
};
// Initialize the choices after getLabelPathFromLabelConfig is defined
// initializeReactFlowNodeChoices(getLabelPathFromLabelConfig);
/**
 * Returns the English plural of a label (for example "Sub-process" becomes "Sub-processes").
 * @param {string} label - Singular label.
 * @returns {string} Plural label.
 * @example
 * pluralizeLabel('Artifact'); // "Artifacts"
 * pluralizeLabel('Sub-process'); // "Sub-processes"
 * pluralizeLabel('Policy'); // "Policies"
 */
export const pluralizeLabel = (label = '') => {
  if (/[^aeiou]y$/i.test(label)) {
    return `${label.slice(0, -1)}ies`;
  }
  if (/(s|x|z|ch|sh)$/i.test(label)) {
    return `${label}es`;
  }
  return `${label}s`;
};
/**
 * Replaces the specified keys in the given item with their corresponding labels based on the provided fieldLabels configuration.
 *
 * The function iterates through the keys in `fieldLabels` and uses regular expressions to replace
 * each key in the `item` with the corresponding `label`. It can be configured to either match whole words
 * or allow partial matches using the `fullWordMatch` flag.
 *
 * @param {string} item - The string item in which the replacements need to be made.
 * @param {Object} fieldLabels - An object where each key is the string to be replaced and its value is an object containing:
 * @param {string} fieldLabels.label - The label to replace the key with.
 * @param {boolean} fieldLabels.fullWordMatch - A boolean indicating whether the key should be replaced only when it matches the whole word.
 *
 * @returns {string} - The transformed string item with replacements made based on `fieldLabels`.
 *
 * @example
 * // Define the fieldLabels configuration
 * const fieldLabels = {
 *   type: { label: 'Template Name', fullWordMatch: true },
 *   association: { label: 'ARTIFACT', fullWordMatch: false }
 * };
 *
 * // Example item string
 * const item = "This type is associated with the association system.";
 *
 * // Replace using the fieldLabels configuration
 * const result = replaceLabel(item, fieldLabels);
 * console.log(result); // Output: "This Template Name is associated with the ARTIFACT system."
 *
 * @example
 * // Example with partial word matching
 * const fieldLabels = {
 *   entity: { label: 'Object', fullWordMatch: false },
 * };
 * const item = "entity_id, entity_code, entity_alias";
 * const result = replaceLabel(item, fieldLabels);
 * console.log(result); // Output: "Object_id, Object_code, Object_alias"
 */
const replaceLabel = (item, fieldLabels) => {
  // If the item is not added in the fieldsLabel object then we will not replace it
  if (!fieldLabels[item]) {
    return item;
  }
  Object.keys(fieldLabels).forEach((key) => {
    const { label, fullWordMatch } = fieldLabels[key];
    const regex = new RegExp(fullWordMatch ? `\\b${key}\\b` : key, 'gi');
    // eslint-disable-next-line no-param-reassign
    item = item.replace(regex, label);
  });
  return item;
};

const FIELD_LABELS = {
  type: { label: 'Template Name', fullWordMatch: true },
  association_name: { label: 'Artifact Name', fullWordMatch: true },
  association_id: { label: 'Association ID', fullWordMatch: true },
  associated_id: { label: 'Associated ID', fullWordMatch: true },
  created_at: { label: 'Created At', fullWordMatch: true },
  updated_at: { label: 'Updated At', fullWordMatch: true },
  updated_by: { label: 'Updated By', fullWordMatch: true },
  created_by: { label: 'Created By', fullWordMatch: true },
  unique_id: { label: 'Unique ID', fullWordMatch: true },
  due_date: { label: 'Due Date', fullWordMatch: true },
  request_type: { label: 'Request Type', fullWordMatch: true },
};

const getDynamicFieldsDependsOnEntityType = () => {
  return {
    model_id: `${getLabelPathFromLabelConfig(
      `TEMPLATE_TYPES.ModelInventory`,
      'featureLabel',
      'Model'
    )} ID`,

    model_name: `${getLabelPathFromLabelConfig(
      `TEMPLATE_TYPES.ModelInventory`,
      'featureLabel',
      'Model'
    )} Name`,

    association_id: `${getLabelPathFromLabelConfig(
      `TEMPLATE_TYPES.ModelAssociation`,
      'featureLabel',
      'Model'
    )} ID`,

    association_name: `${getLabelPathFromLabelConfig(
      `TEMPLATE_TYPES.ModelAssociation`,
      'featureLabel',
      'Model'
    )} NAME`,
    created_at: 'CREATED ON',
    updated_at: 'UPDATED ON',
  };
};
/**
 * Returns a dynamic heading for a given item based on the entity type.
 *
 * @param {string} item - The item to generate a heading for.
 * @param {string} entityType - The type of entity (e.g. 'ModelInventory', 'ModelAssociation').
 * @returns {string} The dynamic heading for the given item.
 * @description This function checks if the item has a dynamic field dependency based on the entity type.
 *              If not, it returns a formatted label for the item.
 */
const getDynamicHeading = (item, entityType) => {
  const DYNAMIC_FIELDS_DEPENDS_ON_ENTITY_TYPE =
    getDynamicFieldsDependsOnEntityType();
  if (DYNAMIC_FIELDS_DEPENDS_ON_ENTITY_TYPE[item]) {
    return DYNAMIC_FIELDS_DEPENDS_ON_ENTITY_TYPE[item];
  }
  if (item === 'Health' || item === 'entity_health') {
    if (entityType === 'ModelInventory') {
      return getLabelPathFromLabelConfig(
        `LABELS?.modelInventory?.table?.column?.ModelHealth`,
        'featureLabel',
        `${getLabelPathFromLabelConfig(
          `TEMPLATE_TYPES.ModelInventory`,
          'featureLabel',
          'Model'
        )} Health`
      );
    }

    if (entityType === 'ModelAssociation') {
      return getLabelPathFromLabelConfig(
        `LABELS?.modelAssociation?.table?.column?.ArtifactHealth`,
        'label',
        `${getLabelPathFromLabelConfig(
          `TEMPLATE_TYPES.ModelAssociation`,
          'featureLabel',
          'Model'
        )} Health`
      );
    }
  }
  return replaceLabel(item, FIELD_LABELS); // Convert item to a more readable label format and make also in uppercase (e.g., "due_date" -> "DUE DATE") ,
};

const isJsonString = (str) => {
  if (isEmpty(str)) {
    return true;
  }
  try {
    JSON.parse(str);
  } catch {
    return false;
  }
  return true;
};

/**
 * @function createDynamicColumns
 * @description function calculate calculate dynamic columns for table and return array of columns
 * @param {Array<String>} data -array of colums names
 * @param {number} width -minimum width of column
 *  @param {function} HeaderCell - type of header
 * @returns  {Array} array of dynamic columns
 */
const createDynamicColumns = ({
  data,
  width,
  HeaderCell,
  excludeCols = EMPTY_ARRAY,
  disabledOrderingCols = EMPTY_ARRAY,
  entityType = null,
  handleOpenFilter = NOOP,
  enableHeaderFilterIcon = false,
  showTimeWithSeconds = false,
}) => {
  const FORMAT = [DD_MM_YYYY_DATE_FORMAT, DATE_FORMAT, YYYY_MM_DD_DATE_FORMAT];

  // Helper function to get column width
  const getColumnWidth = (columnId) => {
    if (columnId === 'Health' || columnId === 'entity_health') {
      return {
        width: 200,
      };
    }
    return width ? { width } : {};
  };

  // Helper function to get entity ID based on entity type
  // const getEntityId = (row) => {
  //   return (
  //     row?.original?.entity_code ||
  //     (row?.original?.entity_type === 'ModelInventory'
  //       ? row?.original?.model_id
  //       : row?.original?.association_id)
  //   );
  // };

  // Helper function to get entity name based on entity type
  const getEntityName = (row) => {
    if (row?.original?.entity_alias) {
      return row?.original?.entity_alias;
    }
    if (row?.original?.entity_type === 'ModelInventory') {
      return row?.original?.model_name;
    }
    return row?.original?.association_name;
  };

  // Helper function to format date
  const formatDate = (date) => {
    // check if date include time or not
    const hasTime = date.includes('T') || date.includes('Z');

    // convert into date and time format if showTimeWithSeconds is true and hasTime is true
    // else use default Date format
    if (showTimeWithSeconds && hasTime) {
      return moment.utc(date).local().format(DATE_FORMAT_HH_MM_SS);
    }
    return moment(date, FORMAT).format(DATE_FORMAT);
  };

  const renderHealthCell = (columnName, row) => {
    if (row?.original?.health_status?.label) {
      return (
        <EntityHealthIndicator
          health={row?.original?.health_status}
          iconSize="18px"
        />
      );
    }
    const flag = row?.original?.[columnName] ?? row?.original?.Health;
    return <AlertHealthIndicator flag={flag} />;
  };

  const getColumnHeader = (props) => {
    const columnId = props?.column?.id;
    const columnLabel = props?.column?.heading;
    const isAssoctionDataType = props?.column?.dataType === 'Association';
    const headerContent =
      columnId === 'Health' || columnId === 'entity_health'
        ? HealthColumnHeader(props)
        : HeaderCell(props);

    if (
      enableHeaderFilterIcon &&
      columnId !== 'Health' &&
      columnId !== 'entity_health' &&
      !isAssoctionDataType
    ) {
      return (
        <ColumnHeaderWithFilter
          columnLabel={columnLabel}
          onFilterClick={handleOpenFilter}
        >
          {headerContent}
        </ColumnHeaderWithFilter>
      );
    }
    return headerContent;
  };

  const renderCell = ({ row, column }) => {
    const item = column?.id;

    const cellValue = row?.original?.[item];
    let parsedCellValue = cellValue;

    if (typeof cellValue === 'string') {
      try {
        parsedCellValue = JSON.parse(cellValue);
      } catch {
        parsedCellValue = cellValue;
      }
    }
    if (column?.dataType === 'Paragraph') {
      return parsedCellValue?.title || '-';
    }

    if (column?.dataType === 'Association') {
      let entityData = {};

      if (typeof cellValue === 'string') {
        if (isJsonString(cellValue)) {
          entityData = JSON.parse(cellValue || '{}');
        } else {
          return cellValue;
        }
      } else {
        entityData = cellValue;
      }
      return isValueEmpty(entityData?.entity_id || entityData?.unique_id)
        ? '-'
        : `${entityData?.unique_id || entityData?.entity_id}`;
    }

    if (column?.dataType === 'User') {
      if (
        column?.input_control_type === 'MULTI_SELECT' &&
        isArray(parsedCellValue)
      ) {
        return parsedCellValue.join(', ') || '-';
      }

      return parsedCellValue || '-';
    }

    if (column?.dataType === 'Group') {
      if (
        column?.input_control_type === 'MULTI_SELECT' &&
        isArray(parsedCellValue)
      ) {
        return parsedCellValue.join(', ') || '-';
      }

      return parsedCellValue || '-';
    }

    if (item === 'Health' || item === 'entity_health') {
      return renderHealthCell(item, row);
    }

    if (item === USERS_WITH_DRAFT) {
      return cellValue === 'True' ? (
        <Box
          display="flex"
          alignItems="center"
          height="100%"
          sx={{ cursor: 'pointer' }}
        >
          <Chip
            label="Unsaved Changes"
            title="Unsaved Changes"
            size="small"
            className="sm-chip md-chip"
            sx={{
              minHeight: '22px',
              fontSize: '12px',
              fontWeight: '600',
            }}
            color="primary"
          />
        </Box>
      ) : (
        '-'
      );
    }

    if (item === 'template_version_number') {
      if (isValueEmpty(cellValue)) return '-';
      return (
        <Box
          display="flex"
          alignItems="center"
          height="100%"
          sx={{ cursor: 'pointer' }}
        >
          <Chip
            label={String(cellValue)}
            title={String(cellValue)}
            size="small"
            className="sm-chip"
            sx={{
              minHeight: '22px',
              fontSize: '12px',
              fontWeight: '600',
            }}
            color="primary"
          />
        </Box>
      );
    }

    if (item === LINKED_ENTITIES) {
      return cellValue ? (
        <LinkedEntitiesCell
          value={cellValue}
          row={row}
          entityType={entityType}
        />
      ) : (
        '-'
      );
    }

    if (item === 'model_name' || item === 'association_name') {
      return getEntityName(row);
    }

    if (isArray(parsedCellValue)) {
      return (
        parsedCellValue
          ?.map((attr) => {
            if (typeof attr === 'string') {
              return attr;
            }

            return attr?.label || attr?.value || '-';
          })
          ?.join(', ') || '-'
      );
    }
    // If the value is in date format, it will be converted into valid format
    if (isDateValid(cellValue)) {
      return formatDate(cellValue);
    }

    if (typeof cellValue === 'number') {
      return isValueEmpty(cellValue) ? '-' : cellValue;
    }

    // If the value is boolean, it will be converted to string as react dose not render boolean values
    if (typeof cellValue === 'boolean') {
      return cellValue.toString();
    }

    // Handle objects with color and value properties for colored cell rendering
    // This type of object we will get from the nimbus side, on nimbus side they are using this to show colored labels in table cells
    if (
      typeof cellValue === 'object' &&
      cellValue?.color &&
      !isValueEmpty(cellValue?.value)
    ) {
      return (
        <Box textAlign="left" bgcolor={cellValue.color}>
          {cellValue.value}
        </Box>
      );
    }

    // Here, if the cell value is an object or empty, we return 'N/A', otherwise we return the cell value itself. Some of the attributes values are not supported to show in table directly so we are returning N/A for those.
    return isValueEmpty(parsedCellValue) || typeof parsedCellValue === 'object'
      ? '-'
      : parsedCellValue;
  };

  return data
    ?.filter((item) => {
      const colName = typeof item === 'string' ? item : item?.field;
      return !excludeCols?.includes(colName);
    })
    ?.map((item, index) => {
      /**
       * we are receiving two types of data here
       * 1. array of objects: [{name: ColumnName, label: ColumnLabel}, ...]
       * 2. array of strings: [ColumnName, ColumnName, ...];
       */
      const colName = typeof item === 'string' ? item : item?.name;
      const columnDataType = typeof item === 'string' ? item : item?.dataType;
      const colLabel =
        typeof item === 'string'
          ? getDynamicHeading(item, entityType)
          : item?.label;

      return {
        Header: getColumnHeader,
        id: colName,
        dataType: columnDataType,
        input_control_type: item?.input_control_type,
        accessor: (value) => {
          if (columnDataType === 'Paragraph') {
            return value?.[colName]?.title || '';
          }

          if (colName === 'model_id' || colName === 'association_id') {
            return (
              value?.entity_code ||
              (value?.entity_type === 'ModelInventory'
                ? value?.model_id
                : value?.association_id)
            );
          }

          if (colName === 'model_name' || colName === 'association_name') {
            return (
              value?.entity_alias ||
              (value?.entity_type === 'ModelInventory'
                ? value?.model_name
                : value?.association_name)
            );
          }

          if (colName === 'Health' || colName === 'entity_health') {
            return value?.health_status?.label;
          }
          return value[colName];
        },

        Cell: renderCell,
        ...(width ? getColumnWidth(colName) : {}),
        heading: colLabel,
        disableOrdering: disabledOrderingCols.includes(colName),
        index,
        disableSortBy: enableHeaderFilterIcon,
      };
    });
};

const createModelFieldObject = (field, suffix, dataType, operators) => {
  return {
    name: field,
    label: `${getLabelPathFromLabelConfig(
      'TEMPLATE_TYPES?.ModelInventory',
      'featureLabel',
      'Model'
    )} ${suffix}`,
    datatype: dataType,
    operators,
  };
};

const createDynamicFilters = (data) => {
  return data.map((value) => {
    const dataType = value?.data_type;
    let operators;

    if (dataType === 'Character') {
      if (value?.field_type === 'MULTI_SELECT') {
        operators = [
          { name: '!=', label: '!=' },
          { name: '=', label: '=' },
          { name: 'in', label: 'In' },
          { name: 'not in', label: 'Not in' },
        ];
      } else {
        operators = [
          { name: '!=', label: '!=' },
          { name: '=', label: '=' },
        ];
      }
    } else if (dataType === 'Date') {
      operators = [
        { name: '<', label: '<' },
        { name: '>', label: '>' },
        { name: '!=', label: '!=' },
        { name: '=', label: '=' },
      ];
    } else if (dataType === 'Integer' || dataType === 'Decimal') {
      operators = [
        { name: '>=', label: '>=' },
        { name: '<=', label: '<=' },
        { name: '<', label: '<' },
        { name: '>', label: '>' },
        { name: '!=', label: '!=' },
        { name: '=', label: '=' },
      ];
    } else {
      operators = [
        { name: '!=', label: '!=' },
        { name: '=', label: '=' },
      ];
    }

    if (value?.name === 'model_id' || value?.name === 'model_name') {
      const suffix = value?.name === 'model_id' ? 'id' : 'name';
      return createModelFieldObject(value?.name, suffix, dataType, operators);
    }

    // if label doesn't exist, pass field
    const rawLabel = value?.label ?? replaceLabel(value?.field, FIELD_LABELS);
    // Create label with capitalized first letter
    const label = rawLabel?.charAt(0)?.toUpperCase() + rawLabel?.slice(1);
    return {
      name: value?.field,
      label,
      datatype: dataType,
      operators,
    };
  });
};

const getTableColumns = (allAttributes) => {
  // some data types are not supported in table yet, so excluding them
  const excludedDataType = new Set([
    'Table',
    'AdvancedTable',
    'Checklist',
    'Document',
    'Chart',
    'HTML',
    'IssueType',
    'InventoryView',
  ]);

  // here we will return an array of object: {name and label}
  return allAttributes
    .filter((item) => !excludedDataType.has(item.data_type))
    .map((item) => ({
      name: item?.name,
      label: item?.label,
      dataType: item?.data_type,
      input_control_type: item?.field_type,
    }));
};

const blockInvalidNumbers = (e, type) => {
  if (type === 'Integer') {
    return ['e', 'E', '+', '-', '.'].includes(e.key) && e.preventDefault();
  }
  return ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault();
};

/**
 * The function converts a default attribute value to a number if the attribute data type is Integer or
 * Decimal.
 * @returns The function `convertDefaultValueToNumber` returns a number value if the
 * `attributeDataType` is either "Integer" or "Decimal" and the `attributeDefaultValue` is a valid
 * number. If the `attributeDefaultValue` is not a valid number, an empty string is returned. If the
 * `attributeDataType` is not "Integer" or "Decimal", the `attributeDefaultValue` is returned as is
 */

const convertDefaultValueToNumber = (
  attributeDataType,
  attributeDefaultValue
) => {
  if (attributeDataType === 'Integer' || attributeDataType === 'Decimal') {
    if (!isValueEmpty(attributeDefaultValue)) {
      // If it's already a number, just return it
      if (typeof attributeDefaultValue === 'number') {
        return !Number.isNaN(attributeDefaultValue)
          ? attributeDefaultValue
          : '';
      }

      // For strings, trim and convert
      if (
        typeof attributeDefaultValue === 'string' &&
        attributeDefaultValue.trim() !== ''
      ) {
        const numberValue = toNumber(attributeDefaultValue);
        if (!Number.isNaN(numberValue)) {
          return numberValue;
        }
      }
    }
    return '';
  }
  if (attributeDataType === 'Association') {
    return null;
  }

  return attributeDefaultValue;
};

const getUniqueUsersList = (usersList) => {
  const uniqueUsers = {};

  return usersList?.filter((user) => {
    if (user?.is_grp) {
      return false;
    }
    if (!uniqueUsers[user?.user]) {
      uniqueUsers[user?.user] = true;
      return true;
    }
    return false;
  });
};

/**
 * @function extractMonth
 *@description  Extracts the month from a date string in UTC format.
 * @param {string} dateString - The date string in UTC format.
 * @returns {string} The full month name extracted from the date.
 */

const extractMonth = (dateString) => {
  const utcDate = moment.utc(dateString, 'YYYY-MM-DDTHH:mm:ss.SSSSSSZ');
  return utcDate.format('MMMM'); // or "MMM" for abbreviated month
};

/**
 * Checks if a given value is present in a string with comma-separated options of drop-down options.
 *
 * @param {string} value - The value to check.
 * @param {string} dropDownOptions - A string with comma-separated options of drop-down options.
 * @returns {boolean} `true` if the value is present in the options or if the value is undefined, `false` otherwise.
 *
 * @example
 * // Example usage:
 * const value = 'abc';
 * const options = 'abc,def,ghi';
 * const result = isValueInDropDownOptions(value, options); // Returns true
 *
 * // Note: The `dropDownOptions` string is split by commas to ensure accurate matching.
 * // This is done to handle cases where default values contain a trailing comma (e.g., 'abc,').
 */
const isValueInDropDownOptions = (value, dropDownOptions) => {
  return !(
    dropDownOptions &&
    value &&
    !dropDownOptions
      ?.split(',')
      .map((item) => item?.trim())
      .filter((item) => item?.length > 0)
      .includes(value?.trim())
  ); // Valid if value is undefined or in dropDownOptions
};

/**
 * Converts comma separated string to array, removes preceding and trailing spaces
 * @param {string} value - comma separated string to be converted to array
 * @returns {Array} Array of strings by removing all preceding and trailing spaces
 */
const toMultiselectArray = (value) => {
  if (!value) {
    return [];
  }
  if (Array.isArray(value)) return value.map((item) => String(item).trim());
  if (typeof value === 'string')
    return value.split(',').map((item) => item.trim());
  return [String(value).trim()];
};

/**
 * @description Format the default value based on data type.
 * @param {object} values - Object containing data type and default value.
 * @returns {string} - Formatted default value.
 */
const formatDefaultValue = (values) => {
  if (values?.data_type === 'Date' && !isEmpty(values?.default_value)) {
    return moment(values?.default_value).format(YYYY_MM_DD_DATE_FORMAT);
  }
  if (values?.data_type === 'Character' && !isEmpty(values?.default_value)) {
    return values?.default_value?.trim();
  }
  return values?.default_value;
};

/**
 * @description Changes the table page index based on the current data, page size, using provided function to go to a page.
 * @param {number} pageIndex - The current page index.
 * @param {Array} data - The data displayed in the table.
 * @param {number} pageSize - The number of items displayed per page.
 * @param {Function} gotoPage - The function to navigate to a specific page.
 */
const changeTablePageIndex = (pageIndex, data, pageSize, gotoPage) => {
  // If we have items equal to page size on the page, we will not going to change the page
  if (data && data.length % pageSize === 0) {
    // Check if the current page index is less than or equal to 0.
    if (pageIndex - 1 <= 0) {
      // If so, go to the first page (index 0).
      gotoPage(0);
    } else {
      // Otherwise, go to the previous page (decrement pageIndex by 1).
      gotoPage(pageIndex - 1);
    }
  }
};

/**
 * Converts a file to Base64 format.
 * @param {File} file - The file to convert to Base64.
 * @returns {Promise<string>} A promise that resolves to the Base64 representation of the file.
 */
const convertFileToBase64 = (file, split = true) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64Data = split ? reader.result.split(',')[1] : reader.result;
      resolve(base64Data);
    };
    reader.onerror = (error) => {
      reject(new Error(error));
    };
    reader.readAsDataURL(file);
  });
};

/**
 * @function renameDuplicates
 * @description Function to rename duplicate columns
 * @param {array}- name of columns
 */
const renameDuplicates = (columns) => {
  const columnCount = {};
  return columns?.map((col) => {
    if (columnCount[col]) {
      columnCount[col] += 1;
      return `${col}_${columnCount[col]}`;
    }
    columnCount[col] = 1;
    return col;
  });
};

/**
 * @function convertToNormalValue
 * @description Converts a given input to a normal value (either a parsed JSON object, trimmed string, or the original input if not in JSON format).
 *
 * @param {string | null | undefined} input - The input to be converted to a normal value.
 * @returns {any} The converted value. It could be a JSON-parsed object, a trimmed string, or the original input.
 */
const convertToNormalValue = (input) => {
  try {
    if (input === null || input === undefined) {
      return null; // Return null for null or undefined inputs
    }

    if (typeof input === 'string') {
      const trimmedInput = input?.trim();
      if (trimmedInput === '') {
        return null; // Return null for empty strings
      }

      // Attempt to parse JSON
      return JSON.parse(trimmedInput);
    }

    // Return input as is for non-string types
    return input;
  } catch {
    // Return the original input for non-JSON-parsable strings
    return typeof input === 'string' ? input.trim() : input;
  }
};

/**
 * @function convertData
 * @description Parses a CSV file and returns the parsed data as an array of objects.
 * Handles empty cells properly by padding missing values with empty strings.
 * @param {string} csvData - The CSV data as a string.
 * @param {number} maxRows - Number of max rows conversion allowed.
 * @param {Function} reject - Function for rejecting promise errors.
 * @returns {Array} An array of objects representing the parsed CSV data.
 */
const convertData = (csvData, maxRows, reject) => {
  const splitCSV = (row) => {
    // Improved CSV parsing to handle empty cells properly
    const cells = [];
    let current = '';
    let inQuotes = false;
    let i = 0;

    while (i < row.length) {
      const char = row[i];
      const nextChar = row[i + 1];

      if (char === '"' && !inQuotes) {
        inQuotes = true;
      } else if (char === '"' && inQuotes) {
        if (nextChar === '"') {
          // Escaped quote
          current += '"';
          i++; // Skip next quote
        } else {
          inQuotes = false;
        }
      } else if (char === ',' && !inQuotes) {
        // End of cell
        cells.push(convertToNormalValue(current.trim()));
        current = '';
      } else {
        current += char;
      }
      i++;
    }

    // Add the last cell
    cells.push(convertToNormalValue(current.trim()));
    return cells;
  };

  const rows = csvData?.split(/\r?\n/).filter(Boolean);

  // Extract and normalize headers
  const formattedHeaders = splitCSV(rows[0]);
  const headers = renameDuplicates(formattedHeaders);
  const jsonData = [];
  if (maxRows < rows?.length) {
    reject({ message: `Max ${maxRows} rows allowed in file.` });
  }

  for (let i = 1; i < rows?.length; i++) {
    const values = splitCSV(rows[i]);

    // Ensure we have the same number of values as headers by padding with empty strings
    while (values.length < headers.length) {
      values.push('');
    }

    // Truncate if we have more values than headers (shouldn't happen with proper CSV)
    if (values.length > headers.length) {
      values.length = headers.length;
    }

    const obj = {};
    for (let j = 0; j < headers.length; j++) {
      obj[headers[j]] = convertToNormalValue(values[j] ?? '');
    }
    jsonData.push(obj);
  }
  if (isArray(jsonData)) {
    return jsonData;
  }
  return [];
};
/**
 * @function convertCsvToJson
 * @description Parses a CSV file and returns the parsed data as an array of objects.
 * Handles empty cells properly and ensures all rows have consistent structure.
 * @param {File} file - The CSV file to parse.
 * @param {number} maxRows - Number of max rows conversion allowed (default value is 100).
 * @returns {Promise<Object[]>} A promise that resolves to the parsed data as an array of objects.
 */
const convertCsvToJson = (file, maxRows = 1001) => {
  return new Promise((resolve, reject) => {
    if (file) {
      const reader = new FileReader();

      reader.onload = (e) => {
        const csvData = e?.target?.result;
        const data = convertData(csvData, maxRows, reject);
        resolve(data);
      };

      reader.onerror = (error) => {
        reject(new Error(`FileReader error: ${error.message}`));
      };
      // Read the file as text.
      reader.readAsText(file, 'utf8');
    }
  });
};

/**
 * Finds orphan nodes in a graph.
 *
 * @param {Array} nodes - List of nodes with unique IDs.
 * @param {Array} edges - List of edges with source and target IDs.
 * @returns {Array} - Orphan nodes (nodes without connecting edges).
 */
const checkOrphanNodes = (nodes, edges) => {
  const nodeIdsWithEdges = new Set();

  edges.forEach((edge) => {
    nodeIdsWithEdges?.add(edge?.source);
    nodeIdsWithEdges?.add(edge?.target);
  });

  return nodes?.filter((node) => !nodeIdsWithEdges?.has(node?.id));
};

/**
 * Checks whether the nodes in a graph have incoming edges.
 * @param {Array} nodes - An array of node objects.
 * @param {Array} edges - An array of edge objects.
 * @param {string} startNodeId - The ID of the node marked as the start node.
 * @returns {boolean} Returns true if all nodes, except the start node, have incoming edges; false otherwise.
 */
const checkIncomingEdges = (nodes, edges, startNodeId) => {
  const targetNodes = new Set(edges?.map((edge) => edge?.target));
  // Check if nodes other than the start node have incoming edges
  const nodesWithIncomingEdges = nodes?.filter(
    (node) => node?.id !== startNodeId && targetNodes?.has(node.id)
  );

  return nodesWithIncomingEdges?.length === nodes.length - 1;
};

/**
 * Checks if there is a path from the start node to the end node in a graph.
 *
 * @param {Array} allNodes - An array of all nodes in the graph.
 * @param {Array} allEdges - An array of all edges in the graph.
 * @param {string} start - The ID of the starting node.
 * @param {string} end - The ID of the ending node.
 * @param {Set} [visitedNodes=new Set()] - A Set to keep track of visited nodes (optional, default is an empty Set).
 * @returns {boolean} - Returns true if a path is found, false otherwise.
 */
const checkPath = (
  allNodes,
  allEdges,
  start,
  end,
  visitedNodes = new Set()
) => {
  visitedNodes?.add(start);
  if (end?.includes(start)) {
    return true;
  }
  const outgoingEdges = allEdges?.filter((edge) => edge?.source === start);
  let pathFound = false;
  outgoingEdges?.forEach((edge) => {
    if (
      !visitedNodes.has(edge?.target) &&
      checkPath(allNodes, allEdges, edge?.target, end, visitedNodes)
    ) {
      pathFound = true;
    }
  });
  if (!pathFound && start === allNodes[0]?.id) {
    return false;
  }

  return pathFound;
};

/**
 * Checks for overlapping nodes in a collection and returns new coordinates
 * with a specified buffer if overlaps are found.
 *
 * @param {Array<Object>} allNodes - An array of node objects to check for overlaps.
 * @param {number} x - The x-coordinate to check for overlaps.
 * @param {number} y - The y-coordinate to check for overlaps.
 * @param {number} buffer - The buffer distance to use for checking overlaps.
 * @returns {Object} - An object containing new coordinates {x, y} adjusted for overlaps.
 */
const checkOverlappingNodes = (allNodes, x, y, buffer) => {
  const isBetween = (a, b, buffer1) => a > b - buffer1 && a < b + buffer1;

  /**
   * Checks if any nodes in an array overlap with specified coordinates (x1, y1).
   *
   * @param {Array<Object>} nodes - An array of node objects to check for overlaps.
   * @param {number} x1 - The x-coordinate to check for overlaps.
   * @param {number} y1 - The y-coordinate to check for overlaps.
   * @returns {boolean} - True if any node overlaps, false otherwise.
   */
  const overlap = (nodes, x1, y1) =>
    nodes.some(
      (node) =>
        isBetween(node?.position?.x, x1, buffer) &&
        isBetween(node?.position?.y, y1, buffer)
    );

  /**
   * Recursively adjusts coordinates (x1, y1) with a buffer to avoid overlaps.
   *
   * @param {Array<Object>} nodes1 - An array of node objects to check for overlaps.
   * @param {number} x1 - The x-coordinate to check for overlaps.
   * @param {number} y1 - The y-coordinate to check for overlaps.
   * @returns {Object} - An object containing new adjusted coordinates {x, y}.
   */
  const overlapChecks = (nodes1, x1, y1) => {
    if (nodes1 && overlap(nodes1, x1, y1)) {
      return overlapChecks(nodes1, x1 + buffer, y1 + buffer);
    }
    return { x: x1, y: y1 };
  };

  return overlapChecks(allNodes, x, y);
};

function isBase64(base64String) {
  try {
    const decodedData = atob(base64String);
    return ['\x89PNG', 'GIF', 'JFIF', 'WEBP', 'RIFF'].some((header) =>
      decodedData.startsWith(header)
    );
  } catch {
    return false;
  }
}

function isArrayofObjects(arr) {
  if (!Array.isArray(arr)) {
    return false;
  }
  if (!arr.length) {
    return false;
  }
  return arr?.every(
    (item) =>
      typeof item === 'object' &&
      item !== null &&
      !('label' in item && 'value' in item)
  );
}

/**
 * Check if a value has the structure { columns: [...], data: [...] }, This is for checking the table datatype value in the logs as we do not have datatype there to detect
 * @param {any} value - The value to check
 * @returns {boolean} - True if value has columns and data properties with correct types
 */
function isTableDataStructure(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  const hasColumns = 'columns' in value && Array.isArray(value.columns);
  const hasData = 'data' in value && Array.isArray(value.data);

  return hasColumns && hasData;
}

function getMonthStartEndDates(date) {
  const momentDate = moment(date);
  if (!momentDate.isValid()) {
    return {};
  }

  // Extract month and year from the input date
  const monthNumber = momentDate.month() + 1; // Add 1 to get the actual month number
  const year = momentDate.year();

  // Create a moment object for the first day and last day of the month
  const startDate = moment(`${year}-${monthNumber}-01`);
  const endDate = startDate.clone().endOf('month');

  return {
    startDate: startDate.format(YYYY_MM_DD_DATE_FORMAT),
    endDate: endDate.format(YYYY_MM_DD_DATE_FORMAT),
  };
}

/**
 * @function scrollToBottom
 * @description Scrolls the containerRef to the bottom with specified behavior.
 * @param {string} [behavior='instant'] - The scroll behavior ('auto', 'smooth', or 'instant').
 * @returns {void}
 */
const scrollToBottom = (containerRef, behavior = 'instant') => {
  if (containerRef.current) {
    containerRef.current.scroll({
      top: containerRef.current.scrollHeight,
      behavior,
    });
  }
};

/**
 * Calculates the date based on the given frequency.
 * @param {string} frequency - The frequency to calculate the date for.
 *   Possible values are 'Monthly', 'Quarterly', or 'Yearly'.
 * @param {Date} date - The reference date.
 * @returns {Date} The calculated date based on the given frequency.
 */
const calculateFrequencyDate = (frequency, date) => {
  let calculatedDate = date;
  switch (frequency) {
    case 'Monthly':
      calculatedDate = moment(date).add(1, 'M');
      break;
    case 'Quarterly':
      calculatedDate = moment(date).add(3, 'M');
      break;
    case 'Yearly':
      calculatedDate = moment(date).add(1, 'Y');
      break;
    default:
      break;
  }

  return calculatedDate;
};

/**
 * Extract user options from model team response.
 * @param {object[]} modelTeam array of model team members
 * @returns {string[]} All unique users in the model team flattened
 */
function extractUserOptionsFromModelTeam(modelTeam) {
  return uniq(
    flatten(
      modelTeam?.map((obj) => (obj?.is_grp ? obj?.group_member : obj?.user))
    )
  );
}

/**
 * Create dynamic template strings
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Template_literals#tagged_templates_and_escape_sequences}
 * @returns {string} formatted string
 * @example
 * const t2Closure = stringFromTemplate`${0} ${"foo"}!`;
 * // const t2Closure = stringFromTemplate([""," ","!"],0,"foo");
 * t2Closure("Hello", { foo: "World" }); // "Hello World!"
 */
function stringFromTemplate(strings, ...keys) {
  return (...values) => {
    const dict = values[values.length - 1] || {};
    const result = [strings[0]];
    keys.forEach((key, i) => {
      const value = Number.isInteger(key) ? values[key] : dict[key];
      result.push(value, strings[i + 1]);
    });
    return result.join('');
  };
}

const transformGridLayoutArray = (gridLayout) => {
  const len = gridLayout.length;
  let sum = 0;
  const ans = Array.from({ length: len }, () => 0);
  let pointer = 0;
  let i;

  for (i = 0; i < len; i += 1) {
    sum += gridLayout[i];

    if (sum === 12) {
      let j;
      for (j = pointer; j <= i; j += 1) {
        ans[j] = gridLayout[j];
      }
      pointer = j;
      sum = 0;
    }

    if (sum > 12) {
      const element = 12 / (i - pointer);
      let j;
      for (j = pointer; j < i; j += 1) {
        ans[j] = Math.floor(element);
      }
      pointer = j;
      sum = 0;
      i -= 1; // NOSONAR
    }
  }

  if (pointer < len) {
    const element = 12 / (i - pointer);
    let j;
    for (j = pointer; j < i; j += 1) {
      ans[j] = Math.floor(element);
    }
  }

  return ans;
};

/**
 * Checks if message content is JSON or string and returns an EditorState accordingly.
 * @param {string} value
 * @returns {EditorState} EditorState for TextEditor component
 */
const getEditorStateFromStringOrJSON = (value) => {
  if (isValueEmpty(value)) {
    return EditorState.createEmpty();
  }
  if (isJsonString(value)) {
    return EditorState.push(
      EditorState.createEmpty(),
      convertFromRaw(JSON.parse(value)),
      'remove-range'
    );
  }
  return EditorState.createWithContent(ContentState.createFromText(value));
};

/**
 * Extracts plain text from Draft.js editor state JSON or returns the string as-is.
 * @param {string} value - Draft.js JSON string or plain text string
 * @returns {string} Plain text content
 */
const getPlainTextFromEditorState = (value) => {
  if (isValueEmpty(value)) {
    return '';
  }
  if (isJsonString(value)) {
    try {
      const contentState = convertFromRaw(JSON.parse(value));
      return contentState.getPlainText()?.trim() || '';
    } catch (error) {
      console.warn('Failed to parse editor state JSON:', error);
      return '';
    }
  }
  return String(value).trim();
};

const hostNeedsOrg = (reqUrl) => {
  return API_ENDPOINTS.HOSTS_NEEDS_TOKEN.includes(
    new URL(reqUrl).host.split(':')[0]
  );
};

const getErrorHelperText = (error, touched) => {
  return touched ? error : null;
};

/**
 * Retrieves an object based on a matching path.
 * Iterates through the provided object and checks if any object's `path` matches the specified path.
 * Returns an object with the matched keys and their corresponding values.
 *
 * @param {Object} obj - The object to be searched.
 * @param {string} path - The path to match with the object's `path` property.
 * @returns {Object} - An object containing the matched keys and their corresponding values.
 */

const getObjectByPath = (obj, path) => {
  let matchedObj = {};
  Object.keys(obj).forEach((objKey) => {
    if (matchPath(path, `/${obj[objKey]?.path}`)) {
      matchedObj = {
        ...matchedObj,
        [objKey]: {
          ...obj[objKey],
        },
      };
    }
  });

  return matchedObj;
};

/**
 * Retrieves the tags for the current route.
 * If a pathname is provided, the function will return tags specific to that route.
 * If no pathname is provided, it returns all tags for all routes.
 *
 * This function first clones the `PATH_TAGS` object, then updates the paths and tag labels dynamically
 * based on configurations found in the `DEFAULT_ROUTE_CONFIG` and `labelConfig` file.
 * After updating, if a `pathname` is provided, it filters the tags to match the given route path.
 *
 * @param {string | undefined} pathname - The route path to retrieve tags for. If not provided, returns tags for all routes.
 * @returns {Array} - A list of tags for the matching route or all routes if no pathname is provided.
 * Each tag is an object containing `label` and `value` properties.
 * @example
 * const tags = getTagsFromRoute('/models/archived');
 * console.log(tags);
 * // Output: [{ label: 'Archived', value: 'archived' }]
 *
 * @example
 * const allTags = getTagsFromRoute();
 * console.log(allTags);
 * // Output: [{ label: 'Archived', value: 'archived' }, { label: 'Active', value: 'active' }]
 */
export const getTagsFromRoute = (pathname) => {
  let clonedPathsTags = cloneDeep(PATH_TAGS);

  //  We are replacing the default values with dynamic one from label config file for PATH_TAGS
  Object.keys(clonedPathsTags).forEach((key) => {
    clonedPathsTags[key].path = getLabelPathFromLabelConfig(
      DEFAULT_ROUTE_CONFIG?.[key]?.keyPath || ``,
      'path',
      clonedPathsTags[key]?.path
    );

    Object.keys(clonedPathsTags[key]?.tags).forEach((tagKey) => {
      clonedPathsTags[key].tags[tagKey] = getLabelPathFromLabelConfig(
        DEFAULT_ROUTE_CONFIG?.[tagKey]?.keyPath || ``,
        'label',
        clonedPathsTags[key]?.tags[tagKey]
      );
    });
  });

  if (!isValueEmpty(pathname)) {
    clonedPathsTags = getObjectByPath(clonedPathsTags, pathname) || {};
  }

  return Object.values(clonedPathsTags).flatMap(({ tags }) =>
    Object.entries(tags).map(([key, label]) => ({
      label,
      value: key,
    }))
  );
};

/**
 * Converts a condition object to a Python expression string.
 *
 * @param {Object} condition - The condition object to convert.
 * @param {Array} condition.rules - The rules within the condition.
 * @returns {string} - The Python expression string.
 */
const convertConditionToPythonExpression = (condition) => {
  /**
   * Processes a field object to a string representation.
   *
   * @param {Object} field - The field object to process.
   * @param {Object} field.fieldType - The type of the field.
   * @param {Object} field.section - The section of the field.
   * @param {Object} field.fieldValue - The value of the field.
   * @returns {string} - The string representation of the field.
   */
  const processField = (field) => {
    const { fieldType, section, fieldValue } = field;
    const sectionPart = section ? `${section?.value}.` : '';
    return `{{${fieldType?.value}.${sectionPart}${fieldValue?.value}}}`;
  };

  /**
   * Processes a rule object to its value representation.
   *
   * @param {Object} rule - The rule object to process.
   * @param {Object} rule.field - The field within the rule.
   * @param {string} rule.value - The value of the rule.
   * @returns {string|number|Array} - The processed value.
   */
  const processValue = (rule) => {
    const dataType = rule?.field?.fieldValue?.data_type;
    const inputControlType = rule?.field?.fieldValue?.input_control_type;

    if (dataType === 'Character') {
      if (
        inputControlType === 'TEXTFIELD' ||
        inputControlType === 'SINGLE_SELECT'
      ) {
        return `"${rule?.value}"`;
      }
      if (inputControlType === 'MULTI_SELECT') {
        return Array.isArray(rule?.value)
          ? `[${rule?.value.map((option) => option?.value)}]`
          : `[]`;
      }
    }

    if (dataType === 'Integer' || dataType === 'Decimal') {
      return Number(rule?.value);
    }

    if (dataType === 'Date') {
      return `"${rule?.value}"`;
    }

    return rule?.value;
  };

  /**
   * Processes a complex rule object to a string representation.
   *
   * @param {Object|string} rule - The rule object or string to process.
   * @returns {string} - The string representation of the complex rule.
   */
  const processComplexRule = (rule) => {
    if (typeof rule === 'string') {
      return rule;
    }

    if (rule?.field === 'userInputKey') {
      return rule?.value;
    }

    if (rule?.field === 'ifConditionKey') {
      const { ifCondition, ifBody, elseBody } = rule?.value || {};
      const ifConditionPart = ifCondition?.rules
        ? // eslint-disable-next-line no-use-before-define
          `if ${processNestedRules(ifCondition?.rules)}`
        : '';
      // eslint-disable-next-line no-use-before-define
      const ifBodyPart = ifBody?.rules ? processNestedRules(ifBody?.rules) : '';
      const elseBodyPart = elseBody?.rules
        ? // eslint-disable-next-line no-use-before-define
          `else ${processNestedRules(elseBody?.rules)}`
        : '';
      return `(${ifBodyPart} ${ifConditionPart} ${elseBodyPart})`.trim();
    }

    return `${processField(rule?.field)} ${rule?.operator} ${processValue(rule)}`;
  };

  /**
   * Processes nested rules to a string representation.
   *
   * @param {Array} nestedRules - The nested rules to process.
   * @returns {string} - The string representation of the nested rules.
   */
  const processNestedRules = (nestedRules) => {
    const result = nestedRules?.map((rule) => {
      if (rule?.rules) {
        return `(${processNestedRules(rule?.rules)})`; // Ensures grouping at all levels
      }
      return processComplexRule(rule);
    });

    // Wrap the entire group in parentheses if it contains more than one rule
    return result.length > 1 ? `(${result.join(' ')})` : result.join(' ');
  };

  /**
   * Processes the main condition object to a string representation.
   *
   * @param {Object} _condition - The main condition object to process.
   * @returns {string} - The string representation of the condition.
   */
  const processCondition = (_condition) => {
    return _condition?.rules
      .map((rule) => {
        if (rule?.rules) {
          return processNestedRules(rule?.rules);
        }
        return processComplexRule(rule);
      })
      .join(' ');
  };

  return isValueEmpty(condition?.rules) ? '' : processCondition(condition);
};

/**
 * Copies the given text to the clipboard.
 *
 * @param {string} text - The text to be copied.
 * @return {Promise<void>} - A promise that resolves when the text is successfully copied.
 */
const copyToClipboard = async (text) => {
  try {
    await window.navigator.clipboard.writeText(text);
    SwalToast({
      icon: 'success',
      title: `Copied to clipboard.`,
    });
  } catch {
    SwalToast({
      icon: 'error',
      title: `Failed to copy to clipboard.`,
    });
  }
};

const updateYupSchemaValidation = (
  yupSchema,
  isRemoveRequired = false,
  dataType = 'Character',
  inputControlType = 'TEXTFIELD'
) => {
  const parsedSchema = parseSafeString(yupSchema, { schema: [{}] });
  const schema = parsedSchema?.schema || [{}];
  const isSpecialSchemaType =
    dataType === 'Paragraph' ||
    dataType === 'Document' ||
    dataType === 'InventoryView' ||
    inputControlType === 'MULTI_SELECT';

  const getRequiredSchema = () => {
    if (dataType === 'Paragraph') {
      return PARAGRAPH_SCHEMA;
    }

    if (
      dataType === 'Document' ||
      dataType === 'InventoryView' ||
      inputControlType === 'MULTI_SELECT'
    ) {
      return ARRAY_SCHEMA;
    }

    return null;
  };

  if (isRemoveRequired) {
    const updatedSchema = schema?.filter(
      (validation) => validation?.type !== 'required'
    );

    if (isSpecialSchemaType) {
      return { schema: [{}] };
    }

    if (updatedSchema?.length === 0) {
      return { schema: [{}] };
    }

    return {
      schema: updatedSchema,
    };
  }

  // to remove empty object form array
  const filteredSchema = schema?.filter(
    (validation) => Object.keys(validation || {})?.length > 0
  );

  // Check if the 'required' validation type exists in the schema array
  const isRequiredPresent = filteredSchema?.some(
    (validation) => validation?.type === 'required'
  );

  // If 'required' is not present, add it to the schema
  if (!isRequiredPresent) {
    const requiredSchema = getRequiredSchema();

    if (requiredSchema) {
      return requiredSchema;
    }

    filteredSchema?.push({
      type: 'required',
      params: ['This field is required'], // Message for required field
    });
  }

  // Return the updated schema with 'required' validation if necessary
  return {
    schema: filteredSchema,
  };
};

const handleRefresh = () => {
  sessionStorage.setItem(HAS_REFRESHED_KEY, 'true');
  window.location.reload();
};
/**
 * React Lazy wrapper function
 * Automatically reloads the window once if browser cannot fetch the corresponding chunk from the server.
 *
 * Why this?
 * After a deployment, the chunk files generated have a different hash from the last deployment.
 * If the platform is already open in the browser, the browser will not know about the latest deployment
 * and tries to fetch the old chunk. Since it cannot get it, the page goes blank.
 * @param {() => Promise<import('react').FC>} dynamicImportFn
 */
export const reactLazyRetry = (dynamicImportFn) =>
  React.lazy(() => {
    return new Promise((resolve, reject) => {
      const hasRefreshed = JSON.parse(
        sessionStorage.getItem(HAS_REFRESHED_KEY) || 'false'
      );
      dynamicImportFn()
        .then((component) => {
          // no refresh needed
          sessionStorage.setItem(HAS_REFRESHED_KEY, 'false');
          resolve(component);
        })
        .catch((error) => {
          // if chunk loading failed and window has not refreshed once,
          // reload the window to get the latest build chunks
          // eslint-disable-next-line no-undef
          if (!hasRefreshed && process.env.NODE_ENV !== 'development') {
            setTimeout(handleRefresh, 3000);
          }
          reject(error);
        });
    });
  });

/**
 * @typedef OutlierTest
 * @property {string} type
 * @property {Array} params
 * @property {string} [message]
 * @property {string} [color = "error"]
 *
 * @typedef OutlierSchema
 * @property {string} type
 * @property {OutlierTest[]} tests
 *
 * @param {any} value
 * @param {OutlierSchema} outlierSchema
 */
export const getOutlierFromOutlierSchema = (value, outlierSchema) => {
  let message;
  let color;
  const baseSchema = Yup[outlierSchema?.type]();

  outlierSchema?.tests?.forEach((test) => {
    const params = test?.params || [];
    const testSchema = baseSchema?.[test?.type](...params);
    const isValid = testSchema?.isValidSync(value);

    if (isValid) {
      message = test?.params[1] || 'Warning';
      color = test?.color || 'error';
    }
  });

  return {
    message,
    color,
  };
};

/**
 * Check if any value is an object with key value pairs
 * Since typeof string and array also returns object
 * This function only returns true if the object has key value pairs
 * @param {any} value
 * @returns if given value is an object with key value pairs
 */
export const isObjectWithKeyValuePairs = (value) =>
  typeof value === 'object' && value !== null && Object.keys(value).length > 0;

/**
 *
 * @param {object} trigger automation trigger
 * @param {object} action automation action
 * @returns if trigger template is equal to action template
 */
export const isTriggerTemplateEqualToActionTemplate = (trigger, action) => {
  const triggerTemplateType = trigger?.parameters?.template_type;
  const triggerTemplateName = trigger?.parameters?.template_name;
  const actionTemplateType = action?.parameters?.template_type;
  const actionTemplateName = action?.parameters?.template_name;

  if (triggerTemplateType !== actionTemplateType) {
    return false;
  }

  return triggerTemplateName === actionTemplateName;
};

/**
 * Calculate utilization rate in percentage given total efforts and available efforts in a week (in days)
 * If total efforts is not given, it defaults to 0 (in days)
 * If available efforts in a week is not given, it defaults to 5 (in days)
 * @param {number} totalEfforts total efforts
 * @param {number} [availableEffortsInWeek=5] available efforts in a week
 * @returns {number} utilization rate in percentage
 */
const calculateUtilizationRate = (totalEfforts) => {
  const availableEffortsInWeek = 5; // in days
  return Math.round((totalEfforts / availableEffortsInWeek) * 100);
};

/**
 * Converts a value into a string representation suitable for use in URL query parameters.
 *
 * @param {*} s - The value to be serialized.
 *   - If `s` is a string, it is URL-encoded.
 *   - If `s` is a number, it is converted to a string.
 *   - If `s` is an object or any other type, it is converted to a JSON string.
 *
 * @returns {string} - The serialized string representation of the input value.
 *
 * @example
 * customSerializeForNumberStringTabValue('hello world'); // "hello%20world"
 * customSerializeForNumberStringTabValue(123); // "123"
 * customSerializeForNumberStringTabValue({ key: 'value' }); // '{"key":"value"}'
 */
const customSerializeForNumberStringTabValue = (s) => {
  if (typeof s === 'string') {
    return s;
  }
  if (typeof s === 'number') {
    return s.toString();
  }
  return JSON.stringify(s);
};

/**
 * Converts a string representation back into its original value.
 *
 * @param {string} s - The string value to be deserialized.
 *   - If `s` can be parsed as JSON, it is parsed into its original type.
 *   - If the parsed value is a string, it is URL-decoded.
 *   - If the parsed value is a number, it is converted to a number type.
 *   - If parsing fails, the original string is returned.
 *
 * @returns {*} - The deserialized value in its original type.
 *
 * @example
 * customDeserializeForNumberStringTabValue('hello%20world'); // "hello world"
 * customDeserializeForNumberStringTabValue('123'); // 123
 * customDeserializeForNumberStringTabValue('{"key":"value"}'); // { key: "value" }
 */
const customDeserializeForNumberStringTabValue = (s) => {
  try {
    const parsed = JSON.parse(s);
    if (typeof parsed === 'string') {
      return decodeURIComponent(parsed);
    }
    if (typeof parsed === 'number') {
      return Number(parsed);
    }
    return parsed;
  } catch {
    return s;
  }
};

export const createSuggestionsFromTagResponse = (input) => {
  const result = [];

  Object.keys(input)
    .map((key) => {
      const value = input[key];
      if (Array.isArray(value)) {
        // Handle arrays like entityGlobal or entityGlobel dynamically
        return value.map((item) => {
          // Check if the item is an object with id and label (for display override)
          if (
            typeof item === 'object' &&
            item !== null &&
            item.id &&
            item.label
          ) {
            return {
              id: `${key}["${item.id}"]`, // backend key for insertion
              label: `${key}["${item.label}"]`, // display label
              originalTag: item.originalTag,
            };
          }
          // Fallback for string items
          return `${key}["${item}"]`;
        });
      }
      if (typeof value === 'object' && value !== null) {
        // Handle nested objects like entityLocal
        return Object.keys(value).flatMap((subKey) => {
          const subValues = value[subKey];
          return subValues.map((subValue) => {
            // Check if the subValue is an object with id and label
            if (
              typeof subValue === 'object' &&
              subValue !== null &&
              subValue.id &&
              subValue.label
            ) {
              return {
                id: `${key}["${subKey}"]["${subValue.id}"]`, // backend key for insertion
                label: `${key}["${subKey}"]["${subValue.label}"]`, // display label
                originalTag: subValue.originalTag,
              };
            }
            // Fallback for string subValues
            return `${key}["${subKey}"]["${subValue}"]`;
          });
        });
      }
      // Return an empty array for unsupported types to avoid undefined
      return [];
    })
    .flat()
    .forEach((item) => {
      result.push(item);
    });
  return result;
};

/**
 * Extracts the last meaningful segment from a URL if its length exceeds 50 characters.
 * If the URL is invalid or shorter than 50 characters, returns the original string.
 *
 * @param {string} url - The URL or string to process.
 * @returns {string} - The cleaned last segment of the URL or the original string.
 */
const getCleanUrlSegment = (url) => {
  let displayText = url;
  try {
    const parsedUrl = new URL(url);
    if (url.length > 50) {
      const pathSegments = parsedUrl.pathname.split('/').filter(Boolean); // Split and filter empty segments
      if (pathSegments.length > 0) {
        const lastSegment = pathSegments[pathSegments.length - 1];
        displayText = decodeURIComponent(lastSegment); // Decode %20 to space
      } else {
        displayText = displayText.slice(0, 50) + '...';
      }
    }
  } catch {
    // Invalid URL; return the original string
  }
  return displayText;
};

/**
 * @function getStatesNameArray
 * @param {array} data
 * @returns {array}
 * @input [{ "id": 1, "name": "ToDo"}, {"id":2,"name": "Development"}]
 * @output ["ToDo", "Development"]
 */

const getStatesNameArray = (data = EMPTY_ARRAY) => {
  if (isValueEmpty(data)) return [];
  return data?.map((state) => {
    return state?.name;
  });
};

export const getAttributeValueColors = (theme) => ({
  Manual: theme?.palette?.text?.manual,
  Automation: theme?.palette?.text?.automation,
  Calculated: theme?.palette?.text?.calculated,
  Overridden: theme?.palette?.text?.overridden,
  Default: theme?.palette?.text?.default,
});

// These keys are defined in frontend code for these entity types, so from backend it is different
const getEntityTypeKey = (entityType, requestType) => {
  if (entityType === 'ModelAssociation') {
    return 'ModelAssociation';
  }

  if (entityType === 'ModelInventory') {
    switch (requestType) {
      case 'ModelRequest':
        return 'modelRequest';
      case 'AccessRequest':
        return 'accessRequest';
      case 'UseCase':
        return 'useCase';
      case 'Query':
        return 'query';
      default:
        return 'modelInventory';
    }
  }
  return 'modelInventory';
};

/**
 * Builds the flat permission key used for template-level inventory permissions.
 *
 * Examples:
 * - ModelAssociation + "Subprocess Template" -> Inventory_modelAssociation_template_Subprocess Template
 * - ModelInventory + ModelRequest + "Assessment Template" -> Inventory_modelRequest_template_Assessment Template
 *
 * @param {string} templateName - Template/entity name from the association config.
 * @param {string} entityType - Backend entity type, such as ModelAssociation or ModelInventory.
 * @param {string} requestType - Request type used to resolve ModelInventory variants.
 * @returns {string} Permission key present in the user permissions object.
 */
const getDynamicEntityPermissionKey = (
  templateName,
  entityType,
  requestType
) => {
  if (!templateName || !entityType) {
    return '';
  }
  const entityTypeKey = getEntityTypeKey(entityType, requestType);
  const entityPermissionKey =
    String(entityTypeKey).charAt(0).toLowerCase() +
    String(entityTypeKey).slice(1);

  return `Inventory_${entityPermissionKey}_template_${templateName}`;
};

/**
 * Filters association templates by template-level permissions.
 *
 * Associations with permission "None" are hidden from issue-type dropdowns.
 * "View", "Manage", and missing permissions are kept visible; action buttons
 * separately decide whether the selected association can be managed.
 *
 * @param {Array<object>} associationList - Associations for the current issue type.
 * @param {object} permissions - Flat permissions object from Redux/user API.
 * @returns {Array<object>} Associations that should remain visible.
 */
const getPermittedAssociationsByPermission = (
  associationList = EMPTY_ARRAY,
  permissions = EMPTY_OBJECT
) => {
  return (associationList || []).filter((entity) => {
    const permissionKey = getDynamicEntityPermissionKey(
      entity?.entity_name,
      entity?.entity_type,
      entity?.request_type
    );

    return permissions?.[permissionKey] !== 'None';
  });
};

const getEntityTypes = (entityType, requestType) => {
  if (entityType === 'ModelAssociation') {
    return entityType;
  }
  if (entityType === 'ModelInventory') {
    if (requestType === 'Normal') {
      return entityType;
    }
    return requestType;
  }
};

/**
 * Returns the label for a given entity type and request type.
 *
 * @param {string} entityType - The type of entity (e.g. 'ModelInventory', 'ModelAssociation').
 * @param {string} [requestType='Normal'] - The type of request (e.g. 'ModelRequest', 'Normal').
 * @returns {string} The label for the given entity and request type.
 */
const getEntityTypeLabel = (entityType, requestType = 'Normal') => {
  const entityTypeKey = getEntityTypes(entityType, requestType);

  if (isValueEmpty(entityTypeKey)) {
    return '-';
  }

  return getLabelPathFromLabelConfig(
    `TEMPLATE_TYPES.${entityTypeKey}`,
    'label',
    entityTypeKey
  );
};

/**
 * Returns formula placeholder tags to use in formula
 *
 * @param {object} placeholderObject Object of available formula variables
 * @param {string} [parentKey = '']
 * @returns Array of formula placeholders to use as suggestions in editor
 *
 */
export const processTemplateFormulaPlaceholders = (
  placeholderObject = EMPTY_OBJECT,
  parentKey = ''
) => {
  const tags = [];

  Object.keys(placeholderObject).forEach((key) => {
    const newKey = parentKey ? `${parentKey}.${key}` : key;
    const value = placeholderObject[key];
    if (Array.isArray(value)) {
      tags.push(...value.map((v) => `${newKey}.${v}`));
    }
    if (isObjectWithKeyValuePairs(value)) {
      tags.push(...processTemplateFormulaPlaceholders(value, newKey));
    }
  });

  return tags;
};

/**
 * Finds the source node from which the transition occurred to reach the final target node.
 *
 * The function processes a list of transformed incoming edges and a mode statuses history.
 * - `transformedIncomingEdges` contains transition data with `source` and `target` values.
 * - `modeStatusesHistory` represents the transition history in reverse order.
 *
 * It determines which `source` in `transformedIncomingEdges` appears in `modeStatusesHistory`
 * as the state before reaching the final `target` (last element in `modeStatusesHistory`).
 */
const findSourceForTarget = (transformedIncomingEdges, modeStatusesHistory) => {
  const target = modeStatusesHistory.at(-1);

  for (let i = modeStatusesHistory.length - 2; i >= 0; i -= 1) {
    const source = modeStatusesHistory[i];
    if (
      transformedIncomingEdges.some(
        (edge) => edge.source === source && edge.target === target
      )
    ) {
      return source;
    }
  }

  return null;
};
const getTemplateType = (newValue) => {
  switch (newValue) {
    case 'ModelInventory':
    case 'ModelRequest':
      return {
        tempType: 'ModelInventory',
        reqType: newValue === 'ModelRequest' ? 'ModelRequest' : 'Normal',
      };
    case 'ModelAssociation':
      return {
        tempType: 'ModelAssociation',
        reqType: 'Normal',
      };
    case 'UseCase':
      return {
        tempType: 'ModelInventory',
        reqType: 'UseCase',
      };
    default:
      return {
        tempType: 'ModelInventory',
        reqType: 'Normal',
      };
  }
};

const showApplicationLauncher = () => {
  try {
    return JSON.parse(localStorage.getItem('REALM_CONFIG'))?.launcher || false;
  } catch {
    return false;
  }
};

const handleInvalidRealm = () => {
  clearAllStorage();
  SwalToast({
    icon: 'error',
    title: 'Please provide valid organization name.',
  });
};

const truncate = (text, maxLength) => {
  return text?.length > maxLength ? text?.slice(0, maxLength) + '...' : text;
};

// Function to find username by user ID
const findUsernameByUserId = (userId, usersList) => {
  if (!userId || !usersList || !Array.isArray(usersList)) {
    return null;
  }

  // Find the user by matching the user ID with item's user_id property
  const user = usersList.find((userItem) => userItem?.user_id === userId);

  return user?.username || null;
};

// Function to find group name by group ID
const findGroupNameByGroupId = (groupId, groupList) => {
  if (!groupId || !groupList || !Array.isArray(groupList)) {
    return null;
  }

  // Find the group by matching the group ID with item's id property
  const group = groupList.find((groupItem) => groupItem?.id === groupId);

  return group?.groupName || null;
};

/**
 * Format an attribute value for display based on its data type
 * @param {any} value - The raw attribute value
 * @param {Object} attributeInfo - The attribute metadata
 * @param {string} attributeInfo.data_type - The data type of the attribute
 * @param {string} attributeInfo.input_control_type - The input control type of the attribute
 * @returns {string|React.ReactNode} - The formatted display value
 */
/**
 * @function getTitle
 * @description Format the cell value for the title attribute of the cell.
 * @param {any} value - The cell value.
 * @param {object} attributeInfo - The attribute information.
 * @param {array} usersList - The list of users.
 * @param {array} groupList - The list of groups.
 * @returns {string} - The formatted title string.
 */
export const getTitle = (value, attributeInfo) => {
  if (isValueEmpty(value)) {
    return '';
  }
  const dataType = attributeInfo?.dataType;
  const inputControlType = attributeInfo?.input_control_type;

  let displayValue = value;

  switch (dataType) {
    case 'Character':
      switch (inputControlType) {
        case 'MULTI_SELECT':
          if (Array.isArray(value)) {
            displayValue = value.map((v) => v?.value).join(', ');
          }
          break;
        case 'SINGLE_SELECT':
          if (name === USERS_WITH_DRAFT) {
            displayValue = value === 'True' ? 'Draft' : '-';
          } else {
            displayValue = value || '-';
          }
          break;
        default:
          displayValue = value.toString();
      }
      break;
    case 'Date': {
      const momentDate = moment(value);
      if (momentDate.isValid()) {
        displayValue = momentDate.format(DATE_FORMAT);
      }
      break;
    }
    case 'Association':
      displayValue = value?.unique_id;
      break;
    case 'User':
      switch (inputControlType) {
        case 'MULTI_SELECT':
          if (Array.isArray(value)) {
            displayValue = value?.map((user) => user)?.join(', ');
          }
          break;
        case 'SINGLE_SELECT':
          displayValue = value;
          break;
      }
      break;
    case 'Group':
      switch (inputControlType) {
        case 'MULTI_SELECT':
          if (Array.isArray(value)) {
            displayValue = value?.map((group) => group)?.join(', ');
          }
          break;
        case 'SINGLE_SELECT':
          displayValue = value;
          break;
      }
      break;
    default:
      if (dataType === 'MultiSelect' && Array.isArray(value)) {
        displayValue = value.join(', ');
      }
      break;
  }

  return typeof displayValue === 'string' ? displayValue : String(displayValue);
};

const dontCheckValuesFor = new Set(['IssueType', 'InventoryView', 'HTML']);

const formatAttributeValue = (
  value,
  attributeInfo,
  hasDraftChanges = false,
  entityId = null,
  entityDetailsFormikForm = EMPTY_OBJECT,
  usersList,
  groupList,
  entityType = null,
  associationTypes = EMPTY_OBJECT,
  isViewMode = false,
  configColumn = false,
  entityData = EMPTY_OBJECT
) => {
  const dataType = attributeInfo?.data_type;
  const isShowAssociationAsTable = attributeInfo?.show_as_table;

  if (attributeInfo?.name === 'entity_health' || isHealthStatusObject(value)) {
    const healthObj = isHealthStatusObject(value)
      ? value
      : attributeInfo?.value;

    if (healthObj?.label) {
      return <EntityHealthIndicator health={healthObj} />;
    }

    const healthFlag = entityData?.Health;
    if (healthFlag) {
      return <AlertHealthIndicator flag={healthFlag} />;
    }

    return '-';
  }

  // Early return for empty values
  if (
    isValueEmpty(value) &&
    !dontCheckValuesFor.has(dataType) &&
    !isShowAssociationAsTable
  ) {
    return '-';
  }

  // Show template version as a chip (single value)
  if (attributeInfo?.name === 'template_version_number') {
    return (
      <Chip
        label={String(value)}
        title={String(value)}
        size="small"
        className="sm-chip"
        sx={{
          minHeight: '22px',
          fontSize: '12px',
          fontWeight: '600',
          maxWidth: '100%',
        }}
        color="primary"
        variant="outlined"
      />
    );
  }

  let displayValue = value;

  const inputControlType = attributeInfo?.input_control_type;
  const inventoryType =
    getEntityTypeKey(attributeInfo?.entityType, attributeInfo?.requestType) ||
    {};

  // First check data type
  switch (dataType) {
    case 'Character':
      // For Character data type, check input control type
      switch (inputControlType) {
        case 'TEXTFIELD':
          displayValue = value.toString();
          break;
        case 'SINGLE_SELECT':
          if (attributeInfo?.name === USERS_WITH_DRAFT) {
            displayValue =
              value === 'True' || hasDraftChanges ? (
                <Chip
                  label="Draft"
                  title="Draft"
                  size="small"
                  className="sm-chip md-chip"
                  sx={{
                    minHeight: '22px',
                    fontSize: '12px',
                    fontWeight: '600',
                  }}
                  color="primary"
                />
              ) : (
                '-'
              );
          } else {
            displayValue = value || '-';
          }
          break;
        case 'MULTI_SELECT':
          if (Array.isArray(value)) {
            displayValue = value.map((v) => v?.value).join(', ');
          } else {
            displayValue = '-';
          }
          break;
        case 'TEXTFIELD_EXTENDED':
          displayValue = value ? (
            <ViewTextFieldExtended value={value} attribute={attributeInfo} />
          ) : (
            '-'
          );
          break;
        default:
          // Default handling for Character
          displayValue = isString(value) ? value : String(value);
      }
      break;

    case 'Integer':
      if (value !== '') {
        displayValue = Number(value).toLocaleString();
      }
      break;

    case 'Decimal':
      if (value !== '') {
        displayValue = Number(value).toLocaleString(undefined, {
          minimumFractionDigits: 2,
        });
      }
      break;

    case 'Date':
      if (value) {
        const momentDate = moment(value);
        if (momentDate.isValid()) {
          displayValue = momentDate.format(DATE_FORMAT);
        } else {
          displayValue = value;
        }
      }
      break;

    case 'MultiSelect':
      if (Array.isArray(value)) {
        displayValue = value.join(', ');
      }
      break;

    case 'Table':
      if (Array.isArray(value?.data)) {
        displayValue = value ? (
          <ViewTable value={value} attribute={attributeInfo} />
        ) : (
          '-'
        );
      }
      break;

    case 'AdvancedTable':
      displayValue = value ? (
        <ViewAdvancedTable value={value} attribute={attributeInfo} />
      ) : (
        '-'
      );
      break;

    case 'Chart':
      displayValue = value ? (
        <ViewChart value={value} attribute={attributeInfo} />
      ) : (
        '-'
      );
      break;

    case 'Checklist':
      displayValue = value ? (
        <ViewChecklist
          value={value}
          attribute={attributeInfo}
          entityId={entityId}
          entityType={entityType}
        />
      ) : (
        '-'
      );
      break;
    case 'Document':
      displayValue = value ? (
        <ViewDocumentAttribute
          documentDetails={value}
          inventoryType={inventoryType}
          isViewMode={isViewMode}
          configColumn={configColumn}
        />
      ) : (
        '-'
      );
      break;
    case 'Paragraph': {
      // if title & justification both not present then in view mode instead of showing blank data updated here it will show "-"
      const hasParagraphValue =
        value && (value?.title?.trim() || value?.description?.trim());

      displayValue = hasParagraphValue ? (
        <ViewParagraph value={value} attribute={attributeInfo} />
      ) : (
        '-'
      );
      break;
    }
    case ATTRIBUTE_DATA_TYPE_HTML:
      displayValue = (
        <LazyHtmlAttributePreviewLink
          attribute={attributeInfo}
          entityId={entityId}
          entityType={entityType}
          showPdfDownload={entityType === ENTITY_TYPE.ModelInventory}
        />
      );
      break;

    case 'Segments':
      displayValue = value ? (
        <SegmentsAttribute
          value={value}
          attribute={attributeInfo}
          viewOnly={true}
          entityData={{
            entity_id: entityId,
            entity_type: attributeInfo?.entityType || 'ModelInventory',
          }}
          entityDetailsFormikForm={entityDetailsFormikForm}
        />
      ) : (
        '-'
      );
      break;

    case 'IssueType':
      displayValue = (
        <IssueTypeComponent
          attributeInfo={attributeInfo}
          associationTypes={associationTypes}
        />
      );
      break;
    case 'InventoryView': {
      displayValue = (
        <InventoryViewAttribute
          mode={'view'}
          value={value}
          attribute={attributeInfo}
          entityDetailsFormikForm={entityDetailsFormikForm}
          entityId={entityId}
          entityType={entityType}
        />
      );
      break;
    }

    case 'Hyperlink': {
      const isValidUrl = (url) => {
        try {
          const parsedUrl = new URL(url);
          return ['http:', 'https:'].includes(parsedUrl?.protocol);
        } catch {
          return false;
        }
      };
      // Normalize URLs like www.example.com (no protocol) by prepending https://
      const normalizedValue =
        value && /^www\./i.test(value) ? `https://${value}` : value;
      const linkHref = isValidUrl(normalizedValue)
        ? normalizedValue
        : `https://${encodeURIComponent(value)}`;
      displayValue = value ? (
        <Link href={linkHref} target="_blank" rel="noopener noreferrer">
          {value}
        </Link>
      ) : (
        '-'
      );
      break;
    }
    case 'Association': {
      if (isShowAssociationAsTable) {
        displayValue = (
          <AssociationFieldTableViewComponent
            attribute={attributeInfo}
            entityId={entityId}
            entityType={entityType}
          />
        );
        break;
      }

      const entityTypeKey = getEntityTypeKey(
        value?.entity_type,
        value?.request_type
      );
      const dynamicEntityPath = getLabelPathFromLabelConfig(
        DEFAULT_ROUTE_CONFIG?.[entityTypeKey]?.keyPath || ``,
        'path',
        DEFAULT_ROUTE_CONFIG?.[entityTypeKey]?.path
      );

      displayValue = value ? (
        <Link
          href={`/${dynamicEntityPath}/${value?.unique_id}`}
          target="_blank"
        >
          <Typography variant="body1">
            {value?.entity_alias}
            &nbsp; ({value?.unique_id})
          </Typography>
        </Link>
      ) : (
        '-'
      );
      break;
    }
    case 'FieldChange':
      displayValue = value ? (
        <FieldChangeOtherDetails
          value={value}
          attribute={attributeInfo}
          mode="view"
          entityDetailsFormikForm={entityDetailsFormikForm}
          usersList={usersList}
          groupList={groupList}
        />
      ) : (
        '-'
      );
      break;
    case 'User': {
      if (value && typeof value === 'number') {
        displayValue = findUsernameByUserId(value, usersList) || value;
      } else if (!isValueEmpty(value)) {
        displayValue = value;
      } else {
        displayValue = '-';
      }
      break;
    }
    case 'Group': {
      if (value && typeof value === 'number') {
        displayValue = findGroupNameByGroupId(value, groupList) || value;
      } else if (!isValueEmpty(value)) {
        displayValue = value;
      } else {
        displayValue = '-';
      }
      break;
    }
    case 'AttributeList': {
      displayValue = value?.label ?? value?.id ?? '-';
      break;
    }
    case 'ParameterTableList': {
      displayValue = value?.name ?? value?.id ?? '-';
      break;
    }
    case 'EmailTemplateList': {
      displayValue = value?.email_template_id
        ? `${value?.email_template_id} (${value?.event_name})`
        : '-';
      break;
    }
    case 'TemplateList': {
      displayValue = value?.entity_name ? value?.entity_name : '-';
      break;
    }
    case 'FieldChangeActions': {
      displayValue = value?.key ? value?.label : '-';
      break;
    }
    default:
      // Handle special cases based on input control type
      if (inputControlType === 'IFRAME') {
        displayValue = value ? (
          <ViewChart value={value} attribute={attributeInfo} />
        ) : (
          '-'
        );
      } else if (typeof value === 'object') {
        // Convert objects to JSON string
        displayValue = JSON.stringify(value);
      }
  }

  return isValueEmpty(displayValue) ? '-' : displayValue;
};

/**
 * Prevents wheel events from changing the numeric input value
 * @function
 * @param {React.WheelEvent} e - The wheel event
 * @returns {void}
 */
const handleWheel = (e) => {
  // Prevent the input value change on scroll
  e.target.blur();
};

/**
 * Prepare attributes with group/section metadata for autocomplete components
 * @param {Object} templateData - Template data containing attributes and sections
 * @param {Array} excludedDataTypes - Array of data types to exclude
 * @returns {Array} Attributes with group_name and group_id metadata
 */
const getTemplateAttributes = (
  templateData,
  excludedDataTypes = [],
  excludeSystemAttribute = false
) => {
  if (!templateData) {
    return [];
  }

  // systemAtt and templateAtts are added to handle attributes during dynamic key generation
  const systemAtt =
    (templateData?.system_attributes || []).map((attr) => ({
      name: attr,
      label: attr,
      value: attr,
      data_type: 'System',
      group_name: 'System Attributes',
      group_id: 'system_attributes',
    })) || [];

  const templateAtts =
    (templateData?.template_attributes || []).map((att) => ({
      ...att,
      label: att?.label || att?.name,
      value: att?.id || att?.name,
      group_name: att?.section || 'Basic Details',
      group_id: att?.section || 'basic_details',
    })) || [];

  const basicInfoAtt =
    templateData?.attributes?.map((att) => ({
      ...att,
      label: att?.label || att?.name,
      value: att?.id || att?.name,
      name: att?.name,
      id: att?.id,
      group_name: 'Basic Details',
      group_id: 'basic_details',
    })) || [];

  const sectionAtt =
    templateData?.sections?.flatMap((section) =>
      section?.attributes?.map((att) => ({
        ...att,
        label: att?.label || att?.name,
        value: att?.id || att?.name,
        name: att?.name,
        id: att?.id,
        group_name: section?.section_name,
        group_id: section?.section_id || section?.section_name,
      }))
    ) || [];

  const allAttributes = [
    ...systemAtt,
    ...templateAtts,
    ...basicInfoAtt,
    ...sectionAtt,
  ];

  if (excludedDataTypes.length === 0 && !excludeSystemAttribute) {
    return allAttributes;
  }

  const added = new Set();
  const filteredAttributes = [];

  allAttributes.forEach((att) => {
    if (
      !added.has(att?.name) &&
      !excludedDataTypes.includes(att?.data_type) &&
      (att?.is_editable === true ||
        att?.is_derived === true ||
        att?.data_type === 'System' ||
        templateData.template_attributes) &&
      (!excludeSystemAttribute || att?.attribute_origin !== 'common')
    ) {
      added.add(att?.name);
      filteredAttributes.push(att);
    }
  });

  return filteredAttributes;
};

/**
 * Normalizes a payload object by converting empty string values to null.
 * @param {Object} body - The payload object to normalize.
 * @returns {Object|null} The normalized object or null if input is falsy.
 */
const normalizePayload = (body) => {
  return body
    ? Object.fromEntries(
        Object.entries(body).map(([key, val]) => [key, val === '' ? null : val])
      )
    : null;
};

/*
 * Extracts authentication parameters from URL (both legacy and secure formats)
 * @param {URLSearchParams} params - URL search parameters
 * @returns {Object} Authentication parameters object
 */

const extractAuthParams = (params) => {
  const encodedAuth = params.get('auth');

  if (encodedAuth) {
    const decodedAuth = SolyticsUtils.decodeURLParams(encodedAuth);
    if (decodedAuth) {
      return decodedAuth;
    }
  }
  // TODO: Remove legacy parameter support once secure params are fully adopted.
  const legacyParams = {
    realm_id: params.get('realm_id') || params.get('realmId'),
    access_token: params.get('access_token'),
    refresh_token: params.get('refresh_token'),
    session_id: params.get('session_id'),
    device_id: params.get('device_id'),
  };
  return legacyParams;
};

// Helper function to convert column index to Excel-style column name (A, B, ..., Z, AA, AB, etc.)
const getColumnName = (columnIndex) => {
  let columnName = '';
  let index = columnIndex;

  while (index > 0) {
    index--; // Convert to 0-based for calculation
    columnName = String.fromCharCode(65 + (index % 26)) + columnName;
    index = Math.floor(index / 26);
  }

  return columnName;
};

// Parse cell ID to get column and row indices
const parseCellId = (cellId) => {
  if (!cellId) return [0, 0];

  // Input validation to prevent ReDoS attacks
  if (typeof cellId !== 'string' || cellId.length > 20) {
    return [0, 0];
  }

  // More specific regex to prevent backtracking - matches Excel-style cell IDs
  const regex = /^([A-Z]{1,3})(\d{1,7})$/;
  const match = regex.exec(cellId);
  if (!match) return [0, 0];

  const colString = match[1];
  const rowNum = parseInt(match[2], 10);

  let colNum = 0;
  for (let i = 0; i < colString.length; i += 1) {
    colNum = colNum * 26 + (colString.charCodeAt(i) - 64);
  }

  return [colNum, rowNum];
};

const formatTimeString = (totalMinutes) => {
  if (!totalMinutes || totalMinutes <= 0) return '';

  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);

  return parts.join(' ');
};

const constructBreadcrumbState = (
  _model,
  _breadcrumbDetails,
  includeAssociationTypeBreadcrumb,
  uniqueId,
  dynamicEntityPath,
  type
) => {
  let breadcrumbState = [];

  if (includeAssociationTypeBreadcrumb) {
    breadcrumbState = [
      ..._breadcrumbDetails,

      {
        label: uniqueId,
        path: `/${dynamicEntityPath}/${uniqueId}`,
        type,
      },
    ];
  } else {
    const modelIndex = _breadcrumbDetails.findIndex(
      (item) => item?.label === uniqueId
    );

    // We will be removing the trailing models from breadcrumb from the redirected model.
    _breadcrumbDetails.splice(modelIndex);

    breadcrumbState = [
      ..._breadcrumbDetails,
      {
        label: uniqueId,
        path: `/${dynamicEntityPath}/${uniqueId}`,
        type,
      },
    ];
  }

  return breadcrumbState;
};

// Check if field is required based on dynamic is_mandatory flag or yup_schema
const checkAttributeIsRequired = (attribute) => {
  // If is_mandatory is explicitly set dynamically, honor it first
  if (
    attribute?.is_mandatory !== undefined &&
    attribute?.is_mandatory !== null
  ) {
    return attribute.is_mandatory === true;
  }

  let schema = attribute?.yup_schema;

  //for paragraph schema
  if (attribute?.data_type === 'Paragraph') {
    schema = schema?.[0]?.yup_schema;
  }

  // Handle case where yup_schema is a string (JSON)
  if (typeof schema === 'string') {
    try {
      const parsed = JSON.parse(schema);
      schema = parsed?.schema || [];
    } catch {
      schema = [];
    }
  }

  // Ensure schema is an array before calling .some()
  if (!Array.isArray(schema)) {
    return false;
  }

  return schema.some((validation) => validation?.type === 'required');
};

/**
 * Filters sections based on permissions, conditional sections, and workflow groups.
 *
 * @param {Object} params - The parameters object.
 * @param {Array} params.sections - The array of sections to filter.
 * @param {Object} params.sectionPermissions - The section permissions object from Redux.
 * @param {string} params.entityCurrentGroupId - The current entity group ID from Redux.
 * @param {string} params.entitySelectedGroupId - The selected entity group ID (optional).
 * @returns {Array} - The filtered array of sections.
 *
 * @example
 * const filteredSections = filterSectionsByPermissions({
 *   sections: formDetails?.sections,
 *   sectionPermissions,
 *   entityCurrentGroupId,
 *   entitySelectedGroupId,
 * });
 */
const filterSectionsByPermissions = ({
  sections = EMPTY_ARRAY,
  sectionPermissions = EMPTY_OBJECT,
  entityCurrentGroupId = '',
  entitySelectedGroupId = '',
  selectedStateNode = '',
  isNodeEdit = false,
}) => {
  return (
    sections?.filter((section) => {
      const secID = section?.section_id;
      /**
       * Node-Specific Logic:
       * When editing or viewing a specific node, the node permission takes precedence.
       * For node editing, we only allow 'manage' and 'view'.
       */
      if (isNodeEdit) {
        if (
          sectionPermissions?.section_permissions?.[secID] !== 'manage' &&
          sectionPermissions?.section_permissions?.[secID] !== 'view'
        ) {
          return false;
        }
      }

      /**
       * if this value is present, then only show sections which are part of selected workflow group
       * but show section in only view mode, doesn't matter whether section have none, view, manage permission or conditional boolean is false
       */
      if (
        !isValueEmpty(entitySelectedGroupId?.groupId) &&
        section?.allow_grouping === true
      ) {
        return section?.show_in_workflow_group?.includes(
          entitySelectedGroupId?.groupId
        );
      }

      if (!isValueEmpty(selectedStateNode)) {
        return sectionPermissions?.section_permissions?.[secID] !== 'none';
      }
      // do not render a section when condition boolean is false
      if (sectionPermissions?.conditional_sections?.[secID] === false) {
        return false;
      }
      // do not render a section when its permission is none
      if (sectionPermissions?.section_permissions?.[secID] === 'none') {
        return false;
      }
      // when grouping is allowed in section, render section which are part of current group
      if (
        section?.allow_grouping === true &&
        !isValueEmpty(entityCurrentGroupId)
      ) {
        return section?.show_in_workflow_group?.includes(entityCurrentGroupId);
      }
      return true;
    }) || []
  );
};

const getTemplateEntityTypeLabel = (entityType, requestType) => {
  const entityTypeKey = getEntityTypes(entityType, requestType);
  return getLabelPathFromLabelConfig(
    `TEMPLATE_TYPES.${entityTypeKey}`,
    'featureLabel',
    entityTypeKey
  );
};

const groupTemplateList = (templateList = EMPTY_ARRAY) => {
  const pluginConfig = Store?.getState()?.tenant?.pluginConfig || {};
  const filteredTemplates = templateList.filter((item) => {
    if (pluginConfig?.hide_useCase && item.request_type === 'UseCase') {
      return false;
    }
    return !(pluginConfig?.hide_query && item.request_type === 'Query');
  });
  const groupedTemplates = filteredTemplates?.map((temp) => ({
    ...temp,
    label: temp?.entity_name,
    value: temp?.id,
    type: getTemplateEntityTypeLabel(temp.entity_type, temp.request_type),
  }));
  groupedTemplates?.sort((a, b) => {
    const typeA = a.type.toLowerCase();
    const typeB = b.type.toLowerCase();

    if (typeA < typeB) {
      return -1;
    }
    if (typeA > typeB) {
      return 1;
    }
    return 0;
  });
  return groupedTemplates;
};

const downloadBlobResponse = (response, fileName) => {
  const blob = new Blob([response?.data]);
  const url = window.URL.createObjectURL(blob);
  const anchorElement = document.createElement('a');
  anchorElement.href = url;
  anchorElement.download = fileName;
  document.body.appendChild(anchorElement);
  anchorElement.click();
  document.body.removeChild(anchorElement);
  window.URL.revokeObjectURL(url);
};

export {
  createYupSchema,
  isValueEmpty,
  createDynamicColumns,
  blockInvalidNumbers,
  isJsonString,
  convertDefaultValueToNumber,
  createDynamicFilters,
  getUniqueUsersList,
  isDateValid,
  extractMonth,
  isValueInDropDownOptions,
  toMultiselectArray,
  formatDefaultValue,
  changeTablePageIndex,
  convertFileToBase64,
  convertCsvToJson,
  isBase64,
  isArrayofObjects,
  isTableDataStructure,
  checkPath,
  checkOverlappingNodes,
  checkOrphanNodes,
  checkIncomingEdges,
  getMonthStartEndDates,
  scrollToBottom,
  calculateFrequencyDate,
  roundToDecimals,
  extractUserOptionsFromModelTeam,
  stringFromTemplate,
  transformGridLayoutArray,
  getEditorStateFromStringOrJSON,
  getPlainTextFromEditorState,
  hostNeedsOrg,
  getErrorHelperText,
  convertConditionToPythonExpression,
  copyToClipboard,
  updateYupSchemaValidation,
  calculateUtilizationRate,
  customSerializeForNumberStringTabValue,
  customDeserializeForNumberStringTabValue,
  getCleanUrlSegment,
  getStatesNameArray,
  getEntityTypeKey,
  getDynamicEntityPermissionKey,
  getPermittedAssociationsByPermission,
  getEntityTypes,
  getEntityTypeLabel,
  findSourceForTarget,
  getTemplateType,
  showApplicationLauncher,
  truncate,
  getTemplateAttributes,
  handleInvalidRealm,
  formatAttributeValue,
  getTableColumns,
  handleWheel,
  findGroupNameByGroupId,
  findUsernameByUserId,
  normalizePayload,
  extractAuthParams,
  getColumnName,
  parseCellId,
  formatTimeString,
  constructBreadcrumbState,
  checkAttributeIsRequired,
  filterSectionsByPermissions,
  groupTemplateList,
  getTemplateEntityTypeLabel,
  formatEntityLabel,
  downloadBlobResponse,
  getAccountId,
};
